import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  AccessibilityInfo,
  View,
  StyleSheet,
  Pressable,
  ActivityIndicator,
  Keyboard,
  Platform,
  Linking,
  Alert,
  Text,
  ScrollView,
  Animated,
  PanResponder,
  Dimensions,
  Modal,
  findNodeHandle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter, useFocusEffect } from 'expo-router';
import { useTheme } from '@/theme';
import { Screen } from '@/components/layout/Screen';
import { SelectField } from '@/components/ui/SelectField';
import { MapView, type MapBounds, type RouteLine, type RouteArrow } from '@/components/maps/MapView';
import { MapNextUpBar } from '@/components/maps/MapNextUpBar';
import { PinDetailModal } from '@/components/maps/PinDetailModal';
import { PinQuickPreview } from '@/components/maps/PinQuickPreview';
import { MapEmptyState } from '@/components/maps/MapEmptyState';
import { MapControlsPanel } from '@/components/maps/MapControlsPanel';
import { MapClickHint } from '@/components/maps/MapClickHint';
import { DirectionsPanel } from '@/components/maps/DirectionsPanel';
import { MapboxSetupModal, shouldShowSetupModal } from '@/components/maps/MapboxSetupModal';
import { MapStyleSelector, getMapboxStyleURL, type MapStyle } from '@/components/maps/MapStyleSelector';
import { CitySelector } from '@/components/maps/CitySelector';
import { DaySelector, type TripDay } from '@/components/maps/DaySelector';
import { DaySummaryCard } from '@/components/maps/DaySummaryCard';
import { DailyDistanceSummary } from '@/components/maps/DailyDistanceSummary';
import { RouteOptimizationModal } from '@/components/maps/RouteOptimizationModal';
import { ItineraryItemForm } from '@/components/itinerary/ItineraryItemForm';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ThemedText, Body, Caption } from '@/components/ui/Text';
import { ErrorState } from '@/components/ui/ErrorState';
import { Ionicons } from '@expo/vector-icons';
import * as Updates from 'expo-updates';
import { copyToClipboard } from '@/utils/clipboard';
import { TripMapMode, useTripMapData } from './useTripMapData';
import { useReferencePointDistances } from './useReferencePointDistances';
import { createItineraryItem, updateItineraryItem } from '@/services/itinerary';
import type { ItineraryItem } from '@/services/itinerary';
import { useUserStore } from '@/state/userStore';
import { usePremiumGateStore } from '@/state/premiumGateStore';
import { hasPremiumAccess } from '@/services/entitlements';
import { isInternalDebugBuild } from '@/services/logger';
import { getCurrentLocation } from '@/services/location';
import { networkMonitor } from '@/services/networkMonitor';
import { updateTripReferencePoints } from '@/services/trips';
import { isMapboxTokenConfigured, getMapboxAccessToken } from '@/config/mapbox';
import type { DistanceDisplayMode, ReferencePointType, MapReferencePoint, ClusterInfo } from '@/types/mapReferences';
import type { MapPin } from '@/components/maps/MapView';
import type { DetailedRoute } from '@/services/navigation';
import { calculateDetailedRoute } from '@/services/navigation';
import type { Place } from '@/services/places';
import { clusterPins } from '@/utils/pinFiltering';
import { convertToClusterInfo, generateClusterLabel } from '@/utils/mapClustering';
import { getDestinationCoordinates, haversineDistance } from '@/utils/geocodeHelpers';
import { getDestinationForDate, toDateKey } from '@/utils/destinationDate';
import { fetchWeatherForecast, getDestinationCoordinates as getWeatherDestinationCoordinates } from '@/services/weather';
import type { DayWeather } from '@/components/maps/DaySummaryCard';
import { toLocalDateKey } from '@/utils/dateKeys';
import { firebaseApp } from '@/lib/firebase';
import { traceModuleEvaluation } from '@/utils/moduleTrace';
import {
  DEFAULT_LANDMARK_DENSITY_PRESET,
  getLandmarkDensityPreset,
} from '@/features/maps/landmarkDensityPresets';
import { getDismissA11yProps } from '@/utils/accessibility';
import { buildExternalDirectionsUrl, buildDirectionsDestinationQuery } from '@/utils/externalMapsQuery';

traceModuleEvaluation('src/features/maps/TripMapExperience');

/** When itinerary pins sit far from the landmark-discovery anchor, include both in "All days" fit. */
const LANDMARK_TRIP_FIT_SPLIT_MIN_KM = 85;
/** Drop itinerary pins this far from the trip/day anchor when computing auto-fit (bad geocodes). */
const MAX_MAP_AUTO_FRAME_KM = 130;

/** Render-time outlier clamp for single-city itinerary pins. */
const SINGLE_CITY_ITINERARY_OUTLIER_MAX_KM = 250;

function isUsableLatLng(lat: number, lng: number): boolean {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    !(lat === 0 && lng === 0) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180
  );
}

function distanceKmToAnchor(
  anchor: { latitude: number; longitude: number },
  lat: number,
  lng: number,
): number {
  try {
    return haversineDistance(
      { lat: anchor.latitude, lng: anchor.longitude },
      { lat, lng },
    );
  } catch {
    return Number.POSITIVE_INFINITY;
  }
}

function parseClockToMinutes(value?: string | null): number | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  if (!normalized || normalized === 'flexible') return null;
  const match = normalized.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number.parseInt(match[1], 10);
  const minutes = Number.parseInt(match[2], 10);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
}

function isSameCalendarDay(date1: Date | string, date2: Date | string): boolean {
  const k1 = toDateKey(date1);
  const k2 = toDateKey(date2);
  return !!k1 && !!k2 && k1 === k2;
}

function formatMinutesToClock(totalMinutes: number): string {
  const clamped = Math.max(0, Math.min(23 * 60 + 59, Math.round(totalMinutes)));
  const hours = Math.floor(clamped / 60);
  const minutes = clamped % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

// Lazy-load bottom sheet to avoid Expo Go crashes when the native module is unavailable.
type BottomSheetModule = {
  default: any;
  BottomSheetView: any;
};

let cachedBottomSheet: BottomSheetModule | null | undefined;
const shouldLoadNativeSheet =
  Platform.OS !== 'web' &&
  (process.env.EXPO_PUBLIC_ENABLE_GORHOM_SHEET === 'true' ||
    process.env.EXPO_PUBLIC_ENABLE_BOTTOM_SHEET === 'true');

const loadBottomSheet = (): BottomSheetModule | null => {
  if (!shouldLoadNativeSheet) return null;
  // Never attempt to load gorhom bottom sheet in Expo Go; it may crash even if
  // the require succeeds due to missing worklets/Reanimated wiring.
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const Constants = require('expo-constants');
    if (Constants?.appOwnership === 'expo' || Constants?.executionEnvironment === 'storeClient') {
      return null;
    }
  } catch {
    // ignore
  }
  // Reanimated guard: avoid loading if worklets are not initialized
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hasWorklets = typeof (globalThis as any).__reanimatedWorkletInit === 'function';
  if (!hasWorklets) return null;
  if (cachedBottomSheet !== undefined) return cachedBottomSheet;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const mod = require('@gorhom/bottom-sheet') as BottomSheetModule;
    cachedBottomSheet = mod;
    return mod;
  } catch {
    cachedBottomSheet = null;
    return null;
  }
};

type TripMapExperienceProps = {
  hideHeader?: boolean;
  isActive?: boolean;
  initialTripId?: string | null;
  controlledTripId?: string | null;
  onTripChange?: (tripId: string | null) => void;
  openOfflineOnLoad?: boolean;
  overlayTopOffset?: number;
};

type TripMapPreviewProps = {
  tripId?: string | null;
  height?: number;
  onOpenFullMap?: () => void;
  title?: string;
  description?: string;
  variant?: 'card' | 'embedded';
};

type ViewportSizeClass = 'small' | 'regular' | 'large';
type ViewportPlatform = 'web' | 'ios' | 'android';

type MapViewportPreset = {
  summaryOverlayTop: number;
  controlsBottomMin: number;
  controlsBottomInset: number;
  recenterBottomMin: number;
  recenterBottomInset: number;
  topOverlayHeight: number;
  topBusyExtra: number;
  topAllDaysExtra: number;
  sidePaddingLeft: number;
  sidePaddingRight: number;
  bottomPaddingAdd: number;
  bottomPaddingMin: number;
  bottomSheetExtra: number;
  fitDayLeft: number;
};

const MAP_VIEWPORT_PRESETS: Record<ViewportPlatform, Record<ViewportSizeClass, MapViewportPreset>> = {
  web: {
    small: {
      summaryOverlayTop: 12,
      controlsBottomMin: 40,
      controlsBottomInset: 40,
      recenterBottomMin: 28,
      recenterBottomInset: 24,
      topOverlayHeight: 140,
      topBusyExtra: 24,
      topAllDaysExtra: 8,
      sidePaddingLeft: 76,
      sidePaddingRight: 74,
      bottomPaddingAdd: 84,
      bottomPaddingMin: 118,
      bottomSheetExtra: 20,
      fitDayLeft: 16,
    },
    regular: {
      summaryOverlayTop: 12,
      controlsBottomMin: 40,
      controlsBottomInset: 40,
      recenterBottomMin: 28,
      recenterBottomInset: 24,
      topOverlayHeight: 140,
      topBusyExtra: 26,
      topAllDaysExtra: 8,
      sidePaddingLeft: 82,
      sidePaddingRight: 80,
      bottomPaddingAdd: 88,
      bottomPaddingMin: 128,
      bottomSheetExtra: 24,
      fitDayLeft: 16,
    },
    large: {
      summaryOverlayTop: 12,
      controlsBottomMin: 40,
      controlsBottomInset: 40,
      recenterBottomMin: 28,
      recenterBottomInset: 24,
      topOverlayHeight: 150,
      topBusyExtra: 26,
      topAllDaysExtra: 8,
      sidePaddingLeft: 88,
      sidePaddingRight: 86,
      bottomPaddingAdd: 92,
      bottomPaddingMin: 134,
      bottomSheetExtra: 24,
      fitDayLeft: 16,
    },
  },
  ios: {
    small: {
      summaryOverlayTop: 12,
      controlsBottomMin: 40,
      controlsBottomInset: 40,
      recenterBottomMin: 28,
      recenterBottomInset: 24,
      topOverlayHeight: 156,
      topBusyExtra: 26,
      topAllDaysExtra: 8,
      sidePaddingLeft: 80,
      sidePaddingRight: 78,
      bottomPaddingAdd: 84,
      bottomPaddingMin: 124,
      bottomSheetExtra: 22,
      fitDayLeft: 16,
    },
    regular: {
      summaryOverlayTop: 12,
      controlsBottomMin: 40,
      controlsBottomInset: 40,
      recenterBottomMin: 28,
      recenterBottomInset: 24,
      topOverlayHeight: 168,
      topBusyExtra: 28,
      topAllDaysExtra: 8,
      sidePaddingLeft: 88,
      sidePaddingRight: 86,
      bottomPaddingAdd: 88,
      bottomPaddingMin: 128,
      bottomSheetExtra: 24,
      fitDayLeft: 16,
    },
    large: {
      summaryOverlayTop: 12,
      controlsBottomMin: 44,
      controlsBottomInset: 44,
      recenterBottomMin: 30,
      recenterBottomInset: 26,
      topOverlayHeight: 180,
      topBusyExtra: 30,
      topAllDaysExtra: 10,
      sidePaddingLeft: 92,
      sidePaddingRight: 90,
      bottomPaddingAdd: 94,
      bottomPaddingMin: 138,
      bottomSheetExtra: 26,
      fitDayLeft: 16,
    },
  },
  android: {
    small: {
      summaryOverlayTop: 14,
      controlsBottomMin: 52,
      controlsBottomInset: 52,
      recenterBottomMin: 42,
      recenterBottomInset: 34,
      topOverlayHeight: 170,
      topBusyExtra: 34,
      topAllDaysExtra: 10,
      sidePaddingLeft: 80,
      sidePaddingRight: 78,
      bottomPaddingAdd: 100,
      bottomPaddingMin: 152,
      bottomSheetExtra: 30,
      fitDayLeft: 14,
    },
    regular: {
      summaryOverlayTop: 14,
      controlsBottomMin: 52,
      controlsBottomInset: 52,
      recenterBottomMin: 42,
      recenterBottomInset: 34,
      topOverlayHeight: 182,
      topBusyExtra: 36,
      topAllDaysExtra: 12,
      sidePaddingLeft: 88,
      sidePaddingRight: 86,
      bottomPaddingAdd: 104,
      bottomPaddingMin: 156,
      bottomSheetExtra: 34,
      fitDayLeft: 14,
    },
    large: {
      summaryOverlayTop: 16,
      controlsBottomMin: 56,
      controlsBottomInset: 56,
      recenterBottomMin: 46,
      recenterBottomInset: 36,
      topOverlayHeight: 194,
      topBusyExtra: 38,
      topAllDaysExtra: 14,
      sidePaddingLeft: 92,
      sidePaddingRight: 90,
      bottomPaddingAdd: 110,
      bottomPaddingMin: 164,
      bottomSheetExtra: 36,
      fitDayLeft: 14,
    },
  },
};

export function TripMapExperience({
  hideHeader = false,
  isActive = true,
  initialTripId = null,
  controlledTripId,
  openOfflineOnLoad = false,
  overlayTopOffset = 0,
  onTripChange,
}: TripMapExperienceProps) {
  const theme = useTheme();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const isMobile = Platform.OS !== 'web';
  const bottomSheetModule = useMemo(() => loadBottomSheet(), []);
  const BottomSheetComponent = bottomSheetModule?.default;
  const BottomSheetViewComponent = bottomSheetModule?.BottomSheetView;
  const nativeBottomSheetAvailable = !!BottomSheetComponent;
  const renderNativeSheet = shouldLoadNativeSheet && nativeBottomSheetAvailable;
  const bottomSheetRef = useRef<any>(null);
  const bottomSheetSnapPoints = useMemo(
    // Allow users to fully collapse the sheet (peek), while keeping a readable default.
    () => (Platform.OS === 'web' ? ['22%', '55%', '88%'] : [72, 160, '48%', '88%']),
    [],
  );
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const authReady = useUserStore((state) => state.authReady);
  const isPremium = useUserStore((state) =>
    hasPremiumAccess({
      isPremium: state.profile?.isPremium,
      planTier: state.profile?.planTier,
      entitlement: state.profile?.entitlement,
    }),
  );
  const openPremiumGate = usePremiumGateStore((s) => s.open);
  const landmarkDensityPreset = getLandmarkDensityPreset(DEFAULT_LANDMARK_DENSITY_PRESET);
  // Never show internal map debug chrome on the public web experience.
  const showStagingDebug = useMemo(
    () => Platform.OS !== 'web' && isInternalDebugBuild(),
    [],
  );
  type MapOverlay =
    | 'none'
    | 'pinModal'
    | 'pinPreview'
    | 'directionsPanel'
    | 'itineraryForm'
    | 'setupModal'
    | 'mapStyleSelector'
    | 'routeOptimization'
    | 'tripPicker'
    | 'mobileQuickActions';
  const [activeOverlay, setActiveOverlay] = useState<MapOverlay>('none');
  const [selectedPin, setSelectedPin] = useState<MapPin | null>(null);
  const [calloutPinId, setCalloutPinId] = useState<string | null>(null);
  const showPinModal = activeOverlay === 'pinModal';
  const setShowPinModal = (visible: boolean) => {
    setActiveOverlay((prev) => (visible ? 'pinModal' : prev === 'pinModal' ? 'none' : prev));
  };
  const showPinPreview = activeOverlay === 'pinPreview';
  const setShowPinPreview = (visible: boolean) => {
    setActiveOverlay((prev) => (visible ? 'pinPreview' : prev === 'pinPreview' ? 'none' : prev));
  };
  const [showControls, setShowControls] = useState(false);
  const [isOnlineNow, setIsOnlineNow] = useState(networkMonitor.isOnline());
  const showDirectionsPanel = activeOverlay === 'directionsPanel';
  const setShowDirectionsPanel = (visible: boolean) => {
    setActiveOverlay((prev) => (visible ? 'directionsPanel' : prev === 'directionsPanel' ? 'none' : prev));
  };
  const [directionsDestination, setDirectionsDestination] = useState<MapPin | null>(null);
  const [currentRoute, setCurrentRoute] = useState<RouteLine | null>(null);
  const [calculatedRoute, setCalculatedRoute] = useState<DetailedRoute | null>(null);
  const showItineraryForm = activeOverlay === 'itineraryForm';
  const setShowItineraryForm = (visible: boolean) => {
    setActiveOverlay((prev) => (visible ? 'itineraryForm' : prev === 'itineraryForm' ? 'none' : prev));
  };
  const [landmarkToAdd, setLandmarkToAdd] = useState<MapPin | null>(null);
  const showSetupModal = activeOverlay === 'setupModal';
  const setShowSetupModal = (visible: boolean) => {
    setActiveOverlay((prev) => (visible ? 'setupModal' : prev === 'setupModal' ? 'none' : prev));
  };
  const [mapboxReady, setMapboxReady] = useState(isMapboxTokenConfigured());
  const showMapStyleSelector = activeOverlay === 'mapStyleSelector';
  const setShowMapStyleSelector = (visible: boolean) => {
    setActiveOverlay((prev) => (visible ? 'mapStyleSelector' : prev === 'mapStyleSelector' ? 'none' : prev));
  };
  const [mapStyle, setMapStyle] = useState<MapStyle>('streets');
  const showRouteOptimization = activeOverlay === 'routeOptimization';
  const setShowRouteOptimization = (visible: boolean) => {
    setActiveOverlay((prev) => (visible ? 'routeOptimization' : prev === 'routeOptimization' ? 'none' : prev));
  };
  const showTripPicker = activeOverlay === 'tripPicker';
  const setShowTripPicker = (visible: boolean) => {
    setActiveOverlay((prev) => (visible ? 'tripPicker' : prev === 'tripPicker' ? 'none' : prev));
  };
  const tripPickerHeadingRef = useRef<View>(null);
  const showMobileQuickActions = activeOverlay === 'mobileQuickActions';
  const setShowMobileQuickActions = (visible: boolean) => {
    setActiveOverlay((prev) => (visible ? 'mobileQuickActions' : prev === 'mobileQuickActions' ? 'none' : prev));
  };
  const [recenterNonce, setRecenterNonce] = useState(0);
  const [zoomAdjustment, setZoomAdjustment] = useState<{ nonce: number; delta: number } | null>(null);
  const zoomCommandNonceRef = useRef(0);
  const [userHasInteractedWithMap, setUserHasInteractedWithMap] = useState(false);
  // Auto-fit guard: on trip switches we want a one-shot fit, but Mapbox can emit zoom/camera events
  // during initial load which we should NOT treat as user interaction (otherwise auto-fit is disabled).
  const pendingAutoFitRef = useRef(false);
  const ignoreZoomEventsUntilRef = useRef<number>(0);
  const prevMapPinsCountRef = useRef(0);
  const dayScopedRefreshAttemptRef = useRef<string | null>(null);
  /** Tracks itinerary pins with valid coords in "All days" mode so we can refit when geocoding adds more. */
  const allDaysItineraryCountRef = useRef<{ tripId: string | null; count: number } | null>(null);
  /** Per-instance dedupe so trip switches always run `refreshPins` (avoid module-level bleed across previews). */
  const lastTripIdThatTriggeredMapRefreshRef = useRef<string | null>(null);
  const [pinFilters, setPinFilters] = useState({
    itinerary: true,
    landmark: true,
    user: true,
    // Temporary/ephemeral map pins (previously search results) are hidden by default.
    search: false,
  });
  const [mustSeeOnly, setMustSeeOnly] = useState(false);
  const [alwaysShowMustSee, setAlwaysShowMustSee] = useState(true);

  const [showRoutes, setShowRoutes] = useState(false);
  const [routeMode, setRouteMode] = useState<'walking' | 'driving' | 'both'>('both');
  const [directionMode, setDirectionMode] = useState<'driving' | 'walking' | 'transit'>('driving');
  const [itineraryRoutes, setItineraryRoutes] = useState<RouteLine[]>([]);
  const [daySequenceRoutes, setDaySequenceRoutes] = useState<RouteLine[]>([]);
  const daySequenceCacheRef = useRef<Map<string, RouteLine[]>>(new Map());
  const [mapDailyWeather, setMapDailyWeather] = useState<Record<string, DayWeather>>({});
  const [weatherStatus, setWeatherStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [weatherError, setWeatherError] = useState<string | null>(null);
  const [weatherReloadNonce, setWeatherReloadNonce] = useState(0);
  const [daySequenceArrows, setDaySequenceArrows] = useState<RouteArrow[]>([]);
  // Day-view "connect-the-dots" route line/arrows are visually awkward and can be expensive.
  // Keep the plumbing but disable rendering/computation for now (per staging feedback).
  const enableDaySequenceRoute = false;
  const [dismissedPromptKey, setDismissedPromptKey] = useState<string | null>(null);
  const [emptyStateDismissed, setEmptyStateDismissed] = useState(false);

  // Extended loading state: show retry UI after 10s of loading
  const [loadingExtended, setLoadingExtended] = useState(false);
  const loadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [fallbackSheetState, setFallbackSheetState] = useState<'peek' | 'half' | 'full'>('half');
  const screenHeight = Dimensions.get('window').height;
  const screenWidth = Dimensions.get('window').width;
  const fallbackHeights = useMemo(
    () => ({
      peek: 72,
      half: Math.min(screenHeight * 0.45, 360),
      full: Math.min(screenHeight * 0.85, 640),
    }),
    [screenHeight],
  );
  const fallbackPan = useRef(new Animated.Value(0)).current;
  const fallbackPanResponder = useMemo(
    () =>
      PanResponder.create({
        onMoveShouldSetPanResponder: (_, gesture) => Math.abs(gesture.dy) > 6,
        onPanResponderMove: (_, gesture) => {
          fallbackPan.setValue(gesture.dy);
        },
        onPanResponderRelease: (_, gesture) => {
          const order: Array<'peek' | 'half' | 'full'> = ['peek', 'half', 'full'];
          const currentIndex = order.indexOf(fallbackSheetState);
          if (gesture.dy > 40 && currentIndex > 0) {
            setFallbackSheetState(order[currentIndex - 1]);
          } else if (gesture.dy < -40 && currentIndex < order.length - 1) {
            setFallbackSheetState(order[currentIndex + 1]);
          }
          Animated.timing(fallbackPan, {
            toValue: 0,
            duration: 150,
            useNativeDriver: true,
          }).start();
        },
      }),
    [fallbackPan, fallbackSheetState],
  );

  useEffect(() => {
    if (!showTripPicker) return;
    const timeout = setTimeout(() => {
      const node = findNodeHandle(tripPickerHeadingRef.current);
      if (node) {
        AccessibilityInfo.setAccessibilityFocus(node);
      }
    }, 60);
    return () => clearTimeout(timeout);
  }, [showTripPicker]);
  const hasOpenedOfflineOnLoad = useRef(false);

  const {
    trips,
    tripsLoading,
    retryLoadTrips,
    tripLoadDebug,
    tripOptions,
    selectedTripId,
    setSelectedTripId,
    mapPins,
    mapCenter,
    currentLocation,
    selectedTrip,
    landmarksRawCount,
    showingCachedLandmarks,
    showingBaselineLandmarks,
    landmarkDataSource,
    landmarkFetchCityLabel,
    landmarkFetchProximity,
    navigationPrompt,
    showNavigationPrompt,
    setShowNavigationPrompt,
    showOfflineDownload,
    setShowOfflineDownload,
    isDownloaded,
    setIsDownloaded,
    notificationsEnabled,
    handleToggleNotifications,
    handleGetDirections,
    isGeocoding,
    isGeocodingPaused,
    itineraryItems,
    itineraryItemsLoading,
    pinsLoading,
    pinsProgress,
    geocodeRateLimited,
    refreshPins,
    refreshLandmarkSuggestions,
    landmarksRefreshing,
    nudgePinsForDisplay,
    // Multi-city trip support
    tripDestinations,
    isMultiCityTrip,
    selectedCityIndex,
    setSelectedCityIndex,
    selectedCityDestination,
    fullTripBounds,
    // Day-based navigation
    tripDays: tripDaysFromHook,
    selectedDayIndex: selectedDayIndexFromHook,
    setSelectedDayIndex: setSelectedDayIndexFromHook,
    selectedDay: selectedDayFromHook,
    selectedDayActivities: selectedDayActivitiesFromHook,
    handleSwipeDayPrev: handleSwipeDayPrevFromHook,
    handleSwipeDayNext: handleSwipeDayNextFromHook,
    missingItineraryItems,
    missingPinsByDay,
  } = useTripMapData({
    initialTripId,
    controlledTripId,
    onTripChange,
    mode: 'full',
    isScreenActive: isActive,
  });

  // If user dismisses the empty overlay (e.g. "Browse landmarks"), reset when trip changes.
  // NOTE: Must be declared after `selectedTripId` is initialized (web TDZ safety).
  useEffect(() => {
    setEmptyStateDismissed(false);
  }, [selectedTripId]);

  // When Maps tab gains focus, nudge map to repaint so pins show without app switch.
  // Multiple nudges give the native map several chances to paint after tab focus.
  useFocusEffect(
    useCallback(() => {
      const t1 = setTimeout(() => nudgePinsForDisplay(), 50);
      const t2 = setTimeout(() => nudgePinsForDisplay(), 200);
      const t3 = setTimeout(() => nudgePinsForDisplay(), 500);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
      };
    }, [nudgePinsForDisplay]),
  );

  const mapDebugString = useMemo(() => {
    const projectId = (firebaseApp as any)?.options?.projectId ?? null;
    const channel = (Updates as any)?.channel ?? null;
    const updateId = (Updates as any)?.updateId ?? null;
    const runtimeVersion = (Updates as any)?.runtimeVersion ?? null;
    return JSON.stringify(
      {
        at: new Date().toISOString(),
        screen: 'TripMapExperience',
        channel,
        updateId,
        runtimeVersion,
        firebaseProjectId: projectId,
        uidPresent: !!firebaseUser?.uid,
        tripsLoading,
        tripsLen: trips?.length ?? null,
        tripLoadDebug,
      },
      null,
      2,
    );
  }, [firebaseUser?.uid, tripsLoading, trips?.length, tripLoadDebug]);

  const handleCopyMapDebug = useCallback(() => {
    void copyToClipboard(mapDebugString).catch(() => {
      // ignore
    });
  }, [mapDebugString]);

  const handleRetryWeather = useCallback(() => {
    setWeatherReloadNonce((n) => n + 1);
  }, []);

  // Track extended loading time - show retry UI after 10s
  useEffect(() => {
    if (tripsLoading) {
      // Start timer when loading begins (reduced from 45s to 10s for better UX)
      loadingTimerRef.current = setTimeout(() => {
        setLoadingExtended(true);
      }, 10000);
    } else {
      // Clear timer and reset extended state when loading ends
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      setLoadingExtended(false);
    }

    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    };
  }, [tripsLoading]);

  // Retry handler - force a resubscribe to trips stream (no navigation required)
  const handleRetryLoad = useCallback(() => {
    setLoadingExtended(false);
    retryLoadTrips?.();
  }, [retryLoadTrips]);

  const showCityBadges = isMultiCityTrip && tripDestinations.length > 1;
  const showCitySelectorRow = isMultiCityTrip && tripDestinations.length > 1 && !showCityBadges;

  const findDestinationForDate = useCallback(
    (date: Date) => getDestinationForDate(tripDestinations, date),
    [tripDestinations],
  );

  const cityCodeFromName = useCallback((city?: string) => {
    if (!city) return undefined;
    const core = city.split(',')[0]?.trim() || city;
    return core.slice(0, 3).toUpperCase();
  }, []);

  const tripDaysComputed = useMemo(() => {
    if (!selectedTrip?.startDate || !selectedTrip?.endDate) return [];
    const start = new Date(selectedTrip.startDate);
    const end = new Date(selectedTrip.endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const days: {
      date: Date;
      dayNumber: number;
      label: string;
      dateLabel: string;
      isToday: boolean;
      isPast: boolean;
      isFuture: boolean;
      activityCount: number;
      hasActivities: boolean;
      cityName?: string;
      cityCode?: string;
      cityIndex?: number;
    }[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let dayNumber = 1;
    const cursor = new Date(start);
    while (cursor <= end) {
      const cursorCopy = new Date(cursor);
      const dayItems = (itineraryItems || []).filter((item) => item.startDate && isSameCalendarDay(item.startDate, cursorCopy));
      const destinationInfo = findDestinationForDate(cursorCopy);
      const cityName = destinationInfo?.destination.city;
      const cityCode = cityCodeFromName(cityName);
      const isToday = isSameCalendarDay(cursorCopy, today);
      const isPast = cursorCopy < today && !isToday;
      const isFuture = cursorCopy > today;

      days.push({
        date: new Date(cursorCopy),
        dayNumber,
        label: `Day ${dayNumber}`,
        dateLabel: cursorCopy.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        isToday,
        isPast,
        isFuture,
        activityCount: dayItems.length,
        hasActivities: dayItems.length > 0,
        cityName,
        cityCode,
        cityIndex: destinationInfo?.index,
      });

      cursor.setDate(cursor.getDate() + 1);
      dayNumber += 1;
    }

    return days;
  }, [selectedTrip?.startDate, selectedTrip?.endDate, itineraryItems, findDestinationForDate, cityCodeFromName]);

  // Local day selection fallback:
  // - `-1` means "All days"
  // - `0..n-1` means a specific day
  // Default to "All" on first open (better UX than forcing Day 1).
  const [selectedDayIndexLocal, setSelectedDayIndexLocal] = useState<number>(-1);

  useEffect(() => {
    if (!tripDaysComputed.length) return;
    // Only clamp overflowing indexes; allow -1 for the "All" tab.
    if (selectedDayIndexLocal >= tripDaysComputed.length) {
      setSelectedDayIndexLocal(-1);
    }
  }, [tripDaysComputed.length, selectedDayIndexLocal]);

  const selectedDayComputed = useMemo(() => {
    if (!tripDaysComputed.length) return null;
    if (selectedDayIndexLocal < 0) return null;
    const idx = Math.min(selectedDayIndexLocal, tripDaysComputed.length - 1);
    return tripDaysComputed[idx] ?? null;
  }, [tripDaysComputed, selectedDayIndexLocal]);

  const selectedDayActivitiesComputed = useMemo(() => {
    if (!selectedDayComputed) return [];
    return (itineraryItems || []).filter((item) => item.startDate && isSameCalendarDay(item.startDate, selectedDayComputed.date));
  }, [itineraryItems, selectedDayComputed]);

  const handleSwipeDayPrevLocal = useCallback(() => {
    setSelectedDayIndexLocal((prev) => {
      if (prev < 0) return -1;
      if (prev == 0) return -1;
      return prev - 1;
    });
  }, []);

  const handleSwipeDayNextLocal = useCallback(() => {
    setSelectedDayIndexLocal((prev) => {
      if (tripDaysComputed.length <= 0) return prev;
      if (prev < 0) return 0;
      return Math.min(tripDaysComputed.length - 1, prev + 1);
    });
  }, [tripDaysComputed.length]);

  const tripDays = tripDaysFromHook ?? tripDaysComputed;
  const selectedDayIndex = selectedDayIndexFromHook ?? selectedDayIndexLocal;
  const setSelectedDayIndex = setSelectedDayIndexFromHook ?? setSelectedDayIndexLocal;
  const selectedDay = selectedDayFromHook ?? selectedDayComputed;
  const selectedDayActivities = selectedDayActivitiesFromHook ?? selectedDayActivitiesComputed;
  const handleSwipeDayPrev = handleSwipeDayPrevFromHook ?? handleSwipeDayPrevLocal;
  const handleSwipeDayNext = handleSwipeDayNextFromHook ?? handleSwipeDayNextLocal;
  const selectedDayKey = useMemo(
    () => (selectedDay && selectedDayIndex >= 0 ? toDateKey(selectedDay.date) : null),
    [selectedDay, selectedDayIndex],
  );

  const tripDaysSafe = tripDays ?? [];

  // Walkability is shown in the day summary card; keep the day picker minimal.

  // Auto-switch city when selecting a day that belongs to another destination
  useEffect(() => {
    if (!showCityBadges) return;
    if (selectedDayIndex < 0) return;
    const day = tripDaysSafe[selectedDayIndex];
    if (day?.cityIndex !== undefined && day.cityIndex !== null && day.cityIndex !== selectedCityIndex) {
      setSelectedCityIndex(day.cityIndex);
    }
  }, [selectedDayIndex, tripDaysSafe, selectedCityIndex, setSelectedCityIndex, showCityBadges]);

  const {
    pinsWithDistances,
    primaryReferencePoint,
    getDistanceToReferencePoint,
  } = useReferencePointDistances({
    pins: mapPins,
    referencePoints: selectedTrip?.referencePoints,
    primaryReferencePointId: selectedTrip?.primaryReferencePointId,
    distanceDisplayMode: selectedTrip?.distanceDisplayMode,
    currentLocation,
  });

  const userId = firebaseUser?.uid;
  const referencePoints = selectedTrip?.referencePoints ?? [];

  const handleSelectReferencePoint = useCallback(
    async (referencePointId: string) => {
      if (!selectedTrip || !userId || referencePoints.length === 0) {
        return;
      }

      try {
        const updatedPoints: MapReferencePoint[] = referencePoints.map((point) => ({
          ...point,
          isPrimary: point.id === referencePointId,
        }));

        await updateTripReferencePoints(selectedTrip.id, userId, {
          referencePoints: updatedPoints,
          primaryReferencePointId: referencePointId,
        });
      } catch (error) {
        console.error('[TripMapExperience] Failed to update primary reference point', error);
        Alert.alert('Unable to update', 'Could not update primary reference point. Please try again.');
      }
    },
    [referencePoints, selectedTrip, userId],
  );

  const handleCreateReferencePoint = useCallback(
    async (input: {
      title: string;
      type: ReferencePointType;
      address?: string;
      latitude: number;
      longitude: number;
    }) => {
      if (!selectedTrip || !userId) {
        return;
      }

      const newReference: MapReferencePoint = {
        id: `ref-${Date.now()}`,
        title: input.title,
        type: input.type,
        address: input.address,
        coordinates: {
          latitude: input.latitude,
          longitude: input.longitude,
        },
        isPrimary: referencePoints.length === 0,
        createdAt: new Date(),
      };

      const updatedPoints = [...referencePoints, newReference];
      const primaryId = referencePoints.length === 0 ? newReference.id : selectedTrip.primaryReferencePointId;

      try {
        await updateTripReferencePoints(selectedTrip.id, userId, {
          referencePoints: updatedPoints,
          primaryReferencePointId: primaryId ?? selectedTrip.primaryReferencePointId,
        });
        Alert.alert('Reference saved', `${input.title} added to your trip.`);
      } catch (error) {
        console.error('[TripMapExperience] Failed to create reference point', error);
        Alert.alert('Unable to add reference', 'Please try again.');
      }
    },
    [referencePoints, selectedTrip, userId],
  );

  const handleDistanceDisplayModeChange = useCallback(
    async (mode: DistanceDisplayMode) => {
      if (!selectedTrip || !userId) {
        return;
      }

      try {
        await updateTripReferencePoints(selectedTrip.id, userId, {
          distanceDisplayMode: mode,
        });
      } catch (error) {
        console.error('[TripMapExperience] Failed to update distance display mode', error);
        Alert.alert('Unable to update', 'Could not change the distance display mode.');
      }
    },
    [selectedTrip, userId],
  );

  // View mode: focus on current city (default) or whole-trip bounds
  const [showFullTripView, setShowFullTripView] = useState(false);

  // Reset auto-fit when trip/city/day changes (so each open feels “smart”).
  // Also clear any callout/modal state that would disable bounds fitting.
  useEffect(() => {
    setUserHasInteractedWithMap(false);
    setCalloutPinId(null);
    setSelectedPin(null);
    setShowPinPreview(false);
    setShowPinModal(false);
    setShowDirectionsPanel(false);
    setDirectionsDestination(null);
    setCurrentRoute(null);
    setCalculatedRoute(null);
    // Force a tiny recenter bump so MapView reacts immediately.
    setRecenterNonce((n) => n + 1);
    // Mark that we're expecting an auto-fit; ignore zoom-change events briefly so they don't
    // flip `userHasInteractedWithMap` back to true before bounds are available.
    pendingAutoFitRef.current = true;
    ignoreZoomEventsUntilRef.current = Date.now() + 2500;
  }, [selectedTripId, selectedCityIndex, selectedDayIndex, showFullTripView]);

  // If requested (from settings), open the offline download sheet on load.
  useEffect(() => {
    if (!openOfflineOnLoad || hasOpenedOfflineOnLoad.current) return;
    if (Platform.OS === 'web') return;
    if (!selectedTrip?.destination) return;
    if (!authReady) return;

    if (!isPremium && !isDownloaded) {
      openPremiumGate({
        feature: 'offline_maps',
        title: 'Offline maps',
        message: 'Offline maps are a Premium feature. Upgrade to download maps for offline use.',
        source: 'feature_gate',
      });
      hasOpenedOfflineOnLoad.current = true;
      return;
    }

    setShowControls(true);
    setShowOfflineDownload(true);
    hasOpenedOfflineOnLoad.current = true;
  }, [openOfflineOnLoad, selectedTrip?.destination, authReady, isPremium, isDownloaded, openPremiumGate]);

  // Check if setup modal should be shown on first load
  useEffect(() => {
    if (__DEV__) {
      shouldShowSetupModal().then((shouldShow) => {
        if (shouldShow) {
          setShowSetupModal(true);
        }
      });
    }
  }, []);

  const handleRetryMapboxCheck = useCallback(() => {
    const ready = isMapboxTokenConfigured();
    setMapboxReady(ready);
    if (!ready) {
      setShowSetupModal(true);
    }
  }, []);

  // Track map zoom level for filtering
  const [mapZoomLevel, setMapZoomLevel] = useState(12);

  // Filter pins based on filter settings and zoom level
  // Use a stable reference to prevent unnecessary re-renders
  const filteredPins = useMemo(() => {
    // Only log when we have pins or when geocoding is complete
    const shouldLog = __DEV__ && (!isGeocoding || pinsWithDistances?.length > 0);

    if (shouldLog) {
      console.log(`[TripMapExperience] Filtering pins: input=${pinsWithDistances?.length || 0}, filters=`, pinFilters, {
        isGeocoding,
        mapPinsCount: mapPins?.length || 0,
      });
    }

    // If geocoding is in progress and we have no pins yet, return empty array silently
    // This is expected during initial load
    if (!pinsWithDistances || pinsWithDistances.length === 0) {
      // Only warn if geocoding is complete and we still have no pins
      if (__DEV__ && !isGeocoding && mapPins && mapPins.length > 0) {
        console.warn(`[TripMapExperience] ⚠️ No pins to filter! pinsWithDistances is empty but mapPins has ${mapPins.length} pins`, {
          mapPins: mapPins.map(p => ({ id: p.id, type: p.type, hasCoords: !!p.coordinates })),
        });
      }
      return [];
    }

    // Integer zoom bands: tiny camera / onZoom jitter was flipping the dense-pin zoom filter and
    // rebuilding the MapView pin set in a tight loop (visible as continual blinking on big trips).
    const zoomForPinFilter = Math.round(mapZoomLevel);

    const isMustSeePin = (pin: MapPin) =>
      pin.highlight === 'must_see' || (pin.badges && pin.badges.includes('Must-see'));

    const mustSeePins =
      alwaysShowMustSee && pinsWithDistances?.length ? pinsWithDistances.filter(isMustSeePin) : [];

    // A small, stable subset of landmarks to keep visible even when zoomed out / de-cluttering.
    // This helps users discover landmarks without needing to zoom in first.
    const featuredLandmarks = (() => {
      const landmarks = pinsWithDistances.filter((p) => p.type === 'landmark');
      if (!landmarks.length) return [];
      const hasItineraryPins = pinsWithDistances.some((p) => p.type === 'itinerary');

      const score = (p: MapPin) => {
        const mustSee = isMustSeePin(p) ? 2 : 0;
        const trending = p.highlight === 'trending' ? 1 : 0;
        return mustSee + trending;
      };

      return landmarks
        .slice()
        .sort((a, b) => {
          const s = score(b) - score(a);
          if (s !== 0) return s;
          return (a.title || '').localeCompare(b.title || '');
        })
        // If there's no itinerary yet, show a bit more so the map feels useful immediately.
        .slice(
          0,
          hasItineraryPins
            ? landmarkDensityPreset.featuredLandmarksWithItinerary
            : landmarkDensityPreset.featuredLandmarksWithoutItinerary,
        );
    })();

    // First apply type filters
    let filtered = pinsWithDistances.filter((pin) => {
      switch (pin.type) {
        case 'itinerary':
          return pinFilters.itinerary;
        case 'landmark':
          return pinFilters.landmark;
        case 'user':
          return pinFilters.user;
        case 'search':
          return pinFilters.search;
        default:
          return true;
      }
    });

    // Optional: must‑sees only
    if (mustSeeOnly) {
      filtered = filtered.filter(
        (pin) => pin.highlight === 'must_see' || (pin.badges && pin.badges.includes('Must-see')),
      );
    } else if (alwaysShowMustSee && mustSeePins.length > 0) {
      // Keep must-see pins even if landmarks are toggled off.
      const merged = new Map<string, MapPin>();
      [...filtered, ...mustSeePins].forEach((p) => merged.set(p.id, p));
      filtered = Array.from(merged.values());
    }

    if (__DEV__) {
      const filteredByType = filtered.length;
      const itineraryCount = filtered.filter(p => p.type === 'itinerary').length;
      console.log(`[TripMapExperience] After type filter: ${filteredByType} pins (${itineraryCount} itinerary)`);

      if (filteredByType === 0 && pinsWithDistances.length > 0) {
        console.warn(`[TripMapExperience] ⚠️ All pins filtered out by type filters!`, {
          inputCount: pinsWithDistances.length,
          filters: pinFilters,
          pinTypes: pinsWithDistances.map(p => p.type),
          itineraryPins: pinsWithDistances.filter(p => p.type === 'itinerary').length
        });
      }
    }

    // Apply zoom-based filtering to reduce clutter at lower zoom levels
    if (filtered.length > 36) {
      const { filterPinsByZoom } = require('@/utils/pinFiltering');
      const beforeZoomFilter = filtered.length;
      filtered = filterPinsByZoom(filtered, Math.max(zoomForPinFilter, 4), {
        showLandmarksAtZoom: landmarkDensityPreset.showLandmarksAtZoom,
        maxPinsAtZoom: landmarkDensityPreset.maxPinsAtZoom,
        minLandmarksAtMediumZoom: landmarkDensityPreset.minLandmarksAtMediumZoom,
      });
      if (__DEV__ && filtered.length < beforeZoomFilter) {
        console.log(`[TripMapExperience] Zoom filter reduced pins from ${beforeZoomFilter} to ${filtered.length} (zoom: ${zoomForPinFilter})`);
      }
    }

    // After zoom-filtering, optionally re-add must-sees so they remain visible when zoomed out.
    if (!mustSeeOnly && alwaysShowMustSee && mustSeePins.length > 0) {
      const merged = new Map<string, MapPin>();
      [...filtered, ...mustSeePins].forEach((p) => merged.set(p.id, p));
      filtered = Array.from(merged.values());
    }

    // Ensure landmarks are consistently discoverable:
    // If de-cluttering removes them (common when zoomed out), keep a small featured subset visible.
    // Respect the landmark toggle and the must-see-only mode.
    const hasAnyLandmarks = filtered.some((p) => p.type === 'landmark');
    if (
      !mustSeeOnly &&
      pinFilters.landmark &&
      featuredLandmarks.length > 0 &&
      (!hasAnyLandmarks || zoomForPinFilter < landmarkDensityPreset.showLandmarksAtZoom + 1)
    ) {
      const merged = new Map<string, MapPin>();
      [...filtered, ...featuredLandmarks].forEach((p) => merged.set(p.id, p));
      filtered = Array.from(merged.values());
    }

    if (__DEV__) {
      console.log(`[TripMapExperience] Final filtered pins: ${filtered.length}`, {
        itinerary: filtered.filter(p => p.type === 'itinerary').length,
        landmark: filtered.filter(p => p.type === 'landmark').length,
        user: filtered.filter(p => p.type === 'user').length,
        search: filtered.filter(p => p.type === 'search').length,
      });
    }

    return filtered;
  }, [pinsWithDistances, pinFilters, mapZoomLevel, mustSeeOnly, alwaysShowMustSee, landmarkDensityPreset]);

  // Create a stable reference for pins to prevent unnecessary MapView re-renders
  // Only update if pin IDs or coordinates actually change
  const stableFilteredPins = useMemo(() => {
    return filteredPins;
  }, [
    // Create a stable key based on pin IDs and coordinates
    // This ensures we only create a new array when pins actually change
    filteredPins.length > 0
      ? filteredPins.map(p => `${p.id}:${p.coordinates.latitude.toFixed(3)},${p.coordinates.longitude.toFixed(3)}`).join('|')
      : '',
    filteredPins.length
  ]);

  // Calculate pin counts
  const pinCounts = useMemo(() => {
    if (!pinsWithDistances || pinsWithDistances.length === 0) {
      return {
        itinerary: 0,
        landmark: 0,
        user: 0,
        search: 0,
      };
    }
    return {
      itinerary: pinsWithDistances.filter((p) => p.type === 'itinerary').length,
      landmark: pinsWithDistances.filter((p) => p.type === 'landmark').length,
      user: pinsWithDistances.filter((p) => p.type === 'user').length,
      search: pinsWithDistances.filter((p) => p.type === 'search').length,
    };
  }, [pinsWithDistances]);

  const mustSeeCount = useMemo(() => {
    if (!pinsWithDistances) return 0;
    return pinsWithDistances.filter((p) => p.highlight === 'must_see' || (p.badges && p.badges.includes('Must-see'))).length;
  }, [pinsWithDistances]);


  const filterBadgeCount = useMemo(() => {
    const total = Object.keys(pinFilters).length;
    const active = Object.values(pinFilters).filter(Boolean).length;
    return active === total ? 0 : active;
  }, [pinFilters]);

  // After a trip switch, rerun pin refresh once per distinct selected id (within this mount).
  useEffect(() => {
    if (!selectedTripId) {
      lastTripIdThatTriggeredMapRefreshRef.current = null;
      return;
    }
    if (lastTripIdThatTriggeredMapRefreshRef.current === selectedTripId) return;
    lastTripIdThatTriggeredMapRefreshRef.current = selectedTripId;
    refreshPins();
  }, [selectedTripId, refreshPins]);

  // Recover Mapbox token asynchronously (e.g., Remote Config) to unblock mobile
  useEffect(() => {
    if (mapboxReady) return;
    let cancelled = false;
    getMapboxAccessToken()
      .then((token) => {
        if (!cancelled && token && token.startsWith('pk.')) {
          setMapboxReady(true);
        }
      })
      .catch(() => {
        // No-op; fallback UI already shown
      });
    return () => {
      cancelled = true;
    };
  }, [mapboxReady]);

  // Calculate activity clusters for visualization
  const clusters = useMemo<ClusterInfo[]>(() => {
    if (!filteredPins || filteredPins.length < 2) {
      return [];
    }

    // Only cluster itinerary pins
    const itineraryPins = filteredPins.filter(pin => pin.type === 'itinerary');
    if (itineraryPins.length < 2) {
      return [];
    }

    const { clusters: pinClusters } = clusterPins(itineraryPins, {
      clusterDistance: 0.5, // 500m
      minClusterSize: 2,
    });

    return pinClusters.map(cluster => {
      const label = generateClusterLabel(cluster, {
        groupByTime: true,
        groupByCategory: true,
      });
      return convertToClusterInfo(cluster, {
        label,
        color: theme.colors.primary + '20',
      });
    });
  }, [filteredPins, theme.colors.primary]);

  // Quantized keys for memo deps only — avoids rebuilding the render pin pipeline on object-identity churn.
  const mapCenterKeyForRendering = useMemo(() => {
    if (!mapCenter || (mapCenter.latitude === 0 && mapCenter.longitude === 0)) return '';
    return `${mapCenter.latitude.toFixed(4)},${mapCenter.longitude.toFixed(4)}`;
  }, [mapCenter?.latitude, mapCenter?.longitude]);

  const primaryReferenceCoordinateKey = useMemo(() => {
    const c = primaryReferencePoint?.coordinates;
    if (!c) return '';
    return `${c.latitude.toFixed(4)},${c.longitude.toFixed(4)}`;
  }, [primaryReferencePoint?.coordinates?.latitude, primaryReferencePoint?.coordinates?.longitude]);

  // Final pin set used by the map: prefer filtered pins, drop invalid coordinates
  // Also filter by selected day when a specific day is selected
  const { pins: mapPinsForRender, hiddenPinsCount, itineraryStepCounts } = useMemo(() => {
    const source = stableFilteredPins && stableFilteredPins.length > 0 ? stableFilteredPins : mapPins || [];
    const steps: { [key: string]: number } = {
      source: source.filter((p) => p.type === 'itinerary').length,
    };

    // First filter for valid coordinates
    let validPins = source.filter((pin) => {
      const lat = pin.coordinates?.latitude;
      const lng = pin.coordinates?.longitude;
      return (
        typeof lat === 'number' &&
        typeof lng === 'number' &&
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        Math.abs(lat) <= 90 &&
        Math.abs(lng) <= 180 &&
        !(lat === 0 && lng === 0)
      );
    });
    steps.afterCoord = validPins.filter((p) => p.type === 'itinerary').length;

    // Prevent cross-trip pin bleed: drop pins whose tripId explicitly belongs to a different trip.
    // We intentionally DO NOT drop pins with null tripId here — during trip loading, pins may be
    // created before the `selectedTrip` memo resolves, leaving tripId=null. Those are handled by:
    //   (a) the trip-switch cleanup effect in useTripMapData (clears foreign pins on switch), and
    //   (b) the single-city clamp below (rejects geographic outliers).
    // User/search pins are always global.
    const activeTripId = selectedTrip?.id || null;
    if (activeTripId) {
      validPins = validPins.filter((pin) => {
        if (pin.type === 'user' || pin.type === 'search') return true;
        if (pin.tripId == null) return true;
        return pin.tripId === activeTripId;
      });
    }
    steps.afterTripId = validPins.filter((p) => p.type === 'itinerary').length;

    // Filter by selected day (if a specific day is selected, not "All Days")
    if (selectedDay && selectedDayIndex >= 0) {
      const beforeDayFilter = validPins;
      const selectedDayKey = toDateKey(selectedDay.date);
      validPins = validPins.filter((pin) => {
        // Always show user location and search pins
        if (pin.type === 'user' || pin.type === 'search') return true;

        // Filter itinerary pins by selected day
        if (pin.type === 'itinerary') {
          if (!selectedDayKey) return true;
          // Prefer the stable dateKey captured at pin-creation time.
          const pinKey = pin.dateKey || toDateKey((pin.data as ItineraryItem | undefined)?.startDate);
          // If we don't have a reliable day key for an itinerary pin, treat it as "missing day metadata"
          // and DO NOT leak it into an arbitrary selected day (this is a main cause of cross-city pins
          // appearing on the wrong day in multi-city trips).
          if (!pinKey) return false;
          return pinKey === selectedDayKey;
        }

        // Show landmarks unless we want to hide them for specific days
        // For now, always show landmarks to provide context
        if (pin.type === 'landmark') return true;

        return true;
      });

      // Guard against transient state where day-filtering would drop all itinerary pins (common during trip/day switches).
      // Only do this if it looks like a transient "missing date metadata" moment; otherwise we'd be showing
      // the wrong day's pins (e.g. Paris pins on a London day).
      const hadItineraryBefore = beforeDayFilter.some((p) => p.type === 'itinerary');
      const hasItineraryAfter = validPins.some((p) => p.type === 'itinerary');
      const hadMissingDayKeysBefore = beforeDayFilter.some((p) => {
        if (p.type !== 'itinerary') return false;
        const k = p.dateKey || toDateKey((p.data as ItineraryItem | undefined)?.startDate);
        return !k;
      });
      if (hadItineraryBefore && !hasItineraryAfter && (!selectedDayKey || hadMissingDayKeysBefore)) {
        validPins = beforeDayFilter;
      }
    }
    steps.afterDay = validPins.filter((p) => p.type === 'itinerary').length;

    // Safety net: for single-city trips, hide itinerary outliers far from the inferred city anchor.
    if (!isMultiCityTrip) {
      const clampAnchor = (() => {
        const fromPair = (lat?: number | null, lng?: number | null) => {
          if (lat == null || lng == null) return null;
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
          if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
          if (lat === 0 && lng === 0) return null;
          return { latitude: lat, longitude: lng };
        };

        const selectedCoords = selectedCityDestination?.coordinates;
        const fromSelected = fromPair(selectedCoords?.latitude, selectedCoords?.longitude);
        if (fromSelected) return fromSelected;

        const firstDestCoords = selectedTrip?.destinations?.[0]?.coordinates;
        const fromFirstDest = fromPair(firstDestCoords?.latitude, firstDestCoords?.longitude);
        if (fromFirstDest) return fromFirstDest;

        const label =
          selectedCityDestination?.city ||
          selectedTrip?.destinations?.[0]?.city ||
          selectedTrip?.destination ||
          selectedTrip?.name;
        if (label) {
          const known = getDestinationCoordinates(label);
          if (known) return { latitude: known.lat, longitude: known.lng };
        }
        return null;
      })();

      if (clampAnchor) {
        const itineraryBefore = validPins.filter((p) => p.type === 'itinerary');
        const withinClamp = itineraryBefore.filter((pin) =>
          haversineDistance(clampAnchor, pin.coordinates) <= SINGLE_CITY_ITINERARY_OUTLIER_MAX_KM,
        );
        // Only apply the clamp if the clamp anchor genuinely agrees with the majority of pins.
        // If it would remove more than half the itinerary, the anchor is almost certainly wrong
        // (e.g. trip name fuzzy-matched the wrong city) and we'd hide the whole trip.
        const majorityAgree =
          itineraryBefore.length === 0 || withinClamp.length * 2 >= itineraryBefore.length;
        if (majorityAgree && withinClamp.length < itineraryBefore.length) {
          const removed = itineraryBefore.length - withinClamp.length;
          validPins = validPins.filter((pin) => {
            if (pin.type !== 'itinerary') return true;
            return (
              haversineDistance(clampAnchor, pin.coordinates) <= SINGLE_CITY_ITINERARY_OUTLIER_MAX_KM
            );
          });
          if (__DEV__) {
            console.warn('[TripMapExperience] Clamped single-city itinerary outliers', {
              removed,
              kept: withinClamp.length,
              anchor: clampAnchor,
              maxKm: SINGLE_CITY_ITINERARY_OUTLIER_MAX_KM,
            });
          }
        } else if (!majorityAgree && __DEV__) {
          console.warn('[TripMapExperience] Skipping single-city clamp (would drop majority)', {
            total: itineraryBefore.length,
            within: withinClamp.length,
            anchor: clampAnchor,
            maxKm: SINGLE_CITY_ITINERARY_OUTLIER_MAX_KM,
          });
        }
      }
    }
    steps.afterClamp = validPins.filter((p) => p.type === 'itinerary').length;

    // If no valid pins, show destination fallback so the map isn't empty
    const fallbackCenter =
      (mapCenter && !(mapCenter.latitude === 0 && mapCenter.longitude === 0)
        ? mapCenter
        : undefined) ||
      (primaryReferencePoint?.coordinates
        ? {
          latitude: primaryReferencePoint.coordinates.latitude,
          longitude: primaryReferencePoint.coordinates.longitude,
        }
        : undefined) ||
      (() => {
        // Never prefer a multi-city label (e.g. "London, Rome & Paris") for coordinate lookup;
        // use an actual city when available.
        const fallbackDestinationLabel =
          selectedCityDestination?.city ||
          selectedTrip?.destinations?.[0]?.city ||
          selectedTrip?.destination;
        const dest = fallbackDestinationLabel?.toLowerCase();
        if (dest) {
          const known = getDestinationCoordinates(dest);
          if (known && !(known.lat === 0 && known.lng === 0)) {
            return { latitude: known.lat, longitude: known.lng };
          }
        }
        // As a last resort, don't force a misleading location
        return undefined;
      })();

    if (validPins.length === 0 && fallbackCenter) {
      validPins.push({
        id: 'destination-fallback',
        tripId: selectedTrip?.id || null,
        title:
          selectedCityDestination?.city ||
          selectedTrip?.destinations?.[0]?.city ||
          selectedTrip?.destination ||
          selectedTrip?.name ||
          'Trip destination',
        coordinates: { latitude: fallbackCenter.latitude, longitude: fallbackCenter.longitude },
        type: 'landmark',
      });
    }

    // Cap pins for performance. Fill itinerary stops first, then closest discovery pins.
    // (Previously all pins were sorted by distance together, which could hide far-but-valid activities.)
    const MAX_TOTAL_PINS = selectedDayIndex >= 0 ? 320 : 260;
    const keep: typeof validPins = [];
    const always = validPins.filter((p) => p.type === 'user' || p.type === 'search');
    always.forEach((p) => keep.push(p));

    const others = validPins.filter((p) => p.type !== 'user' && p.type !== 'search');
    const itineraryOthers = others.filter((p) => p.type === 'itinerary');
    const nonItineraryOthers = others.filter((p) => p.type !== 'itinerary');
    let hidden = 0;

    let room = Math.max(0, MAX_TOTAL_PINS - keep.length);
    const itineraryKept = itineraryOthers.slice(0, Math.min(itineraryOthers.length, room));
    keep.push(...itineraryKept);
    hidden += itineraryOthers.length - itineraryKept.length;

    room = Math.max(0, MAX_TOTAL_PINS - keep.length);
    if (room > 0 && nonItineraryOthers.length > 0) {
      const centerForCap =
        (mapCenter && !(mapCenter.latitude === 0 && mapCenter.longitude === 0) ? mapCenter : undefined) ||
        (primaryReferencePoint?.coordinates
          ? { latitude: primaryReferencePoint.coordinates.latitude, longitude: primaryReferencePoint.coordinates.longitude }
          : undefined) ||
        null;

      const score = (pin: (typeof nonItineraryOthers)[number]) => {
        const anchor =
          pin.type === 'landmark' && landmarkFetchProximity && isUsableLatLng(
            landmarkFetchProximity.latitude,
            landmarkFetchProximity.longitude,
          )
            ? landmarkFetchProximity
            : centerForCap;
        if (!anchor) return 0;
        return haversineDistance(
          { lat: anchor.latitude, lng: anchor.longitude },
          pin.coordinates,
        );
      };
      const sorted = [...nonItineraryOthers].sort((a, b) => score(a) - score(b));
      let landmarkPins = sorted.filter((pin) => pin.type === 'landmark');
      const otherPins = sorted.filter((pin) => pin.type !== 'landmark');

      // Day view priority: do not allow discovery landmarks to sit directly on top of itinerary stops.
      // This keeps planned stops visible when both datasets contain the same/nearby coordinates.
      if (selectedDayIndex >= 0 && itineraryOthers.length > 0) {
        const OVERLAP_HIDE_KM = 0.03; // ~30m
        const HARD_OVERLAP_HIDE_KM = 0.012; // ~12m (almost same physical point)
        const normalize = (value?: string) =>
          (value || '')
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        const likelySamePlace = (landmarkPin: MapPin, itineraryPin: MapPin) => {
          const lTitle = normalize(landmarkPin.title);
          const iTitle = normalize(itineraryPin.title);
          if (!lTitle || !iTitle) return false;
          return lTitle === iTitle || lTitle.includes(iTitle) || iTitle.includes(lTitle);
        };

        const originalLandmarks = landmarkPins;
        const dedupedLandmarks = landmarkPins.filter((landmarkPin) => {
          return !itineraryOthers.some((itineraryPin) => {
            const km = haversineDistance(itineraryPin.coordinates, landmarkPin.coordinates);
            if (km <= HARD_OVERLAP_HIDE_KM) return true;
            const nearDuplicate = km <= OVERLAP_HIDE_KM;
            return nearDuplicate && likelySamePlace(landmarkPin, itineraryPin);
          });
        });
        // Never wipe landmarks entirely in day view; keep a tiny fallback set if all were considered duplicates.
        landmarkPins =
          dedupedLandmarks.length > 0
            ? dedupedLandmarks
            : originalLandmarks.slice(0, Math.min(2, originalLandmarks.length));
      }

      // Reserve a small landmark budget so landmarks remain discoverable in dense itineraries.
      const landmarkReserve = Math.min(
        landmarkPins.length,
        room,
        selectedDayIndex >= 0
          ? landmarkDensityPreset.finalLandmarkReserveDayView
          : landmarkDensityPreset.finalLandmarkReserveAllDays,
      );
      const reservedLandmarks = landmarkPins.slice(0, landmarkReserve);
      keep.push(...reservedLandmarks);

      room = Math.max(0, MAX_TOTAL_PINS - keep.length);
      const remainingPool = [...otherPins, ...landmarkPins.slice(landmarkReserve)].sort(
        (a, b) => score(a) - score(b),
      );
      const take = Math.min(room, remainingPool.length);
      keep.push(...remainingPool.slice(0, take));

      const nonItineraryKept = reservedLandmarks.length + take;
      hidden += nonItineraryOthers.length - nonItineraryKept;
    } else if (room === 0 && nonItineraryOthers.length > 0) {
      hidden += nonItineraryOthers.length;
    }

    steps.afterCap = keep.filter((p) => p.type === 'itinerary').length;

    if (__DEV__) {
      console.log('[TripMapExperience] pins to render:', keep.length, { hidden, itinerarySteps: steps });
    }

    return { pins: keep, hiddenPinsCount: hidden, itineraryStepCounts: steps };
  }, [
    mapPins,
    stableFilteredPins,
    mapCenterKeyForRendering,
    selectedTrip,
    selectedCityDestination?.city,
    selectedCityDestination?.coordinates,
    primaryReferenceCoordinateKey,
    selectedDay,
    selectedDayIndex,
    isMultiCityTrip,
    landmarkDensityPreset,
    landmarkFetchProximity,
  ]);

  const landmarkPinsInMapCount = useMemo(
    () => (mapPins || []).filter((p) => p.type === 'landmark').length,
    [mapPins],
  );
  const landmarkPinsAfterFilterCount = useMemo(
    () => (filteredPins || []).filter((p) => p.type === 'landmark').length,
    [filteredPins],
  );
  const landmarkPinsRenderedCount = useMemo(
    () => mapPinsForRender.filter((p) => p.type === 'landmark').length,
    [mapPinsForRender],
  );
  const itineraryPinsInMapCount = useMemo(
    () => (mapPins || []).filter((p) => p.type === 'itinerary').length,
    [mapPins],
  );
  const itineraryPinsAfterFilterCount = useMemo(
    () => (filteredPins || []).filter((p) => p.type === 'itinerary').length,
    [filteredPins],
  );
  const itineraryPinsRenderedCount = useMemo(
    () => mapPinsForRender.filter((p) => p.type === 'itinerary').length,
    [mapPinsForRender],
  );
  const landmarkDebugText = useMemo(() => {
    const lm = `LM r:${landmarksRawCount} m:${landmarkPinsInMapCount} f:${landmarkPinsAfterFilterCount} d:${landmarkPinsRenderedCount} s:${landmarkDataSource}`;
    const it = `IT m:${itineraryPinsInMapCount} f:${itineraryPinsAfterFilterCount} d:${itineraryPinsRenderedCount}`;
    const s = itineraryStepCounts || {};
    const stepStr = `src:${s.source ?? '?'} c:${s.afterCoord ?? '?'} t:${s.afterTripId ?? '?'} d:${s.afterDay ?? '?'} cl:${s.afterClamp ?? '?'} cp:${s.afterCap ?? '?'}`;
    return `${lm} • ${it} • ${stepStr}`;
  }, [
    landmarksRawCount,
    landmarkPinsInMapCount,
    landmarkPinsAfterFilterCount,
    landmarkPinsRenderedCount,
    itineraryPinsInMapCount,
    itineraryPinsAfterFilterCount,
    itineraryPinsRenderedCount,
    landmarkDataSource,
    itineraryStepCounts,
  ]);
  const landmarkDebugQueryText = useMemo(() => {
    const city = landmarkFetchCityLabel || 'n/a';
    const prox = landmarkFetchProximity
      ? `${landmarkFetchProximity.latitude.toFixed(2)},${landmarkFetchProximity.longitude.toFixed(2)}`
      : '?';
    return `Q city:${city} prox:${prox}`;
  }, [
    landmarkFetchCityLabel,
    landmarkFetchProximity,
  ]);

  // When pin count increases (e.g. after geocoding), nudge map to repaint so pins show without app switch.
  useEffect(() => {
    const count = mapPinsForRender.length;
    const prev = prevMapPinsCountRef.current;
    prevMapPinsCountRef.current = count;
    if (Platform.OS === 'web' || count <= prev) return;
    const t1 = setTimeout(() => nudgePinsForDisplay(), 100);
    const t2 = setTimeout(() => nudgePinsForDisplay(), 400);
    setRecenterNonce((n) => n + 1);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [mapPinsForRender.length, mapPinsForRender, nudgePinsForDisplay]);

  const selectedDayItineraryPins = useMemo(() => {
    if (!selectedDay || selectedDayIndex < 0) return [];
    const selectedDayKey = toDateKey(selectedDay.date);
    return mapPinsForRender.filter((pin) => {
      if (pin.type !== 'itinerary') return false;
      if (!selectedDayKey) return false;
      const pinKey = pin.dateKey || toDateKey((pin.data as ItineraryItem | undefined)?.startDate);
      if (!pinKey) return false;
      return pinKey === selectedDayKey;
    });
  }, [mapPinsForRender, selectedDay, selectedDayIndex]);

  // Day view UX: if this day still has missing itinerary pins after the current run settles,
  // run one full-trip geocode pass (prioritizing the selected day). Scoped-only runs caused
  // inconsistent pins and skipped persisting coords for other days.
  useEffect(() => {
    if (!selectedTripId || !selectedDayKey) return;
    if (pinsLoading || isGeocoding) return;
    if (!selectedDayActivities?.length) return;
    if (selectedDayItineraryPins.length >= selectedDayActivities.length) return;

    const key = `${selectedTripId}:${selectedDayKey}`;
    if (dayScopedRefreshAttemptRef.current === key) return;
    dayScopedRefreshAttemptRef.current = key;
    refreshPins(selectedDayKey, { onlyPriorityDay: false });
  }, [
    isGeocoding,
    pinsLoading,
    refreshPins,
    selectedDayActivities,
    selectedDayItineraryPins.length,
    selectedDayKey,
    selectedTripId,
  ]);

  const preferredTravelMode = useMemo<'driving' | 'walking' | 'transit'>(() => {
    const fromTrip = (selectedTrip as any)?.preferredTravelMode as 'driving' | 'walking' | 'transit' | undefined;
    return fromTrip || directionMode || 'driving';
  }, [selectedTrip, directionMode]);

  const getStartMinutesForPin = useCallback((pin: MapPin): number => {
    const item = pin.data as ItineraryItem | undefined;
    const t = (item?.startTime || '').trim();
    if (!t || t === 'flexible') return 9999; // flexible items at end
    const m = /^(\d{1,2}):(\d{2})$/.exec(t);
    if (!m) return 9999;
    const hh = Number(m[1]);
    const mm = Number(m[2]);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return 9999;
    return hh * 60 + mm;
  }, []);

  const selectedDayItineraryPinsOrdered = useMemo(() => {
    if (!selectedDay || selectedDayIndex < 0) return [];
    const pins = [...selectedDayItineraryPins];
    pins.sort((a, b) => {
      const am = getStartMinutesForPin(a);
      const bm = getStartMinutesForPin(b);
      if (am !== bm) return am - bm;
      const aTitle = (a.title || '').toLowerCase();
      const bTitle = (b.title || '').toLowerCase();
      if (aTitle !== bTitle) return aTitle.localeCompare(bTitle);
      return a.id.localeCompare(b.id);
    });
    return pins;
  }, [getStartMinutesForPin, selectedDay, selectedDayIndex, selectedDayItineraryPins]);

  const pinsWithDayIndexes = useMemo<MapPin[]>(() => {
    if (selectedDayIndex < 0 || selectedDayItineraryPinsOrdered.length === 0) return mapPinsForRender;
    const idxMap = new Map<string, number>();
    selectedDayItineraryPinsOrdered.forEach((p, idx) => idxMap.set(p.id, idx));
    return mapPinsForRender.map((p) => {
      const idx = idxMap.get(p.id);
      if (idx === undefined) return p;
      return { ...p, index: idx };
    });
  }, [mapPinsForRender, selectedDayIndex, selectedDayItineraryPinsOrdered]);

  // "All days" auto-fit: the first fit often runs while only a subset of pins have coordinates.
  // Mapbox then emits zoom events that set `userHasInteractedWithMap`, which freezes bounds.
  // When more itinerary pins become geocoded, refit so the overview shows every stop.
  useEffect(() => {
    if (selectedDayIndex >= 0) {
      allDaysItineraryCountRef.current = null;
      return;
    }
    const tripId = selectedTripId ?? null;
    const itineraryWithCoords = pinsWithDayIndexes.filter((p) => {
      if (p.type !== 'itinerary') return false;
      const c = p.coordinates;
      if (!c || !Number.isFinite(c.latitude) || !Number.isFinite(c.longitude)) return false;
      if (c.latitude === 0 && c.longitude === 0) return false;
      return Math.abs(c.latitude) <= 90 && Math.abs(c.longitude) <= 180;
    }).length;

    const slot = allDaysItineraryCountRef.current;
    if (!slot || slot.tripId !== tripId) {
      allDaysItineraryCountRef.current = { tripId, count: itineraryWithCoords };
      return;
    }
    if (itineraryWithCoords > slot.count) {
      allDaysItineraryCountRef.current = { tripId, count: itineraryWithCoords };
      setUserHasInteractedWithMap(false);
      setRecenterNonce((n) => n + 1);
      pendingAutoFitRef.current = true;
      ignoreZoomEventsUntilRef.current = Date.now() + 2500;
    } else if (itineraryWithCoords < slot.count) {
      allDaysItineraryCountRef.current = { tripId, count: itineraryWithCoords };
    }
  }, [pinsWithDayIndexes, selectedDayIndex, selectedTripId]);

  // Compute Mapbox-routed path between sequential itinerary pins when a day is selected.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!enableDaySequenceRoute) {
        setDaySequenceRoutes((prev) => (prev.length === 0 ? prev : []));
        return;
      }
      if (!selectedTrip?.id || selectedDayIndex < 0) {
        setDaySequenceRoutes((prev) => (prev.length === 0 ? prev : []));
        return;
      }
      if (selectedDayItineraryPinsOrdered.length < 2) {
        setDaySequenceRoutes((prev) => (prev.length === 0 ? prev : []));
        return;
      }

      const dayKey = selectedDay ? toDateKey(selectedDay.date) : `day-${selectedDayIndex}`;
      const ids = selectedDayItineraryPinsOrdered.map((p) => p.id).join(',');
      const cacheKey = `${selectedTrip.id}|${dayKey}|${preferredTravelMode}|${ids}`;
      const cached = daySequenceCacheRef.current.get(cacheKey);
      if (cached) {
        setDaySequenceRoutes(cached);
        return;
      }

      const routeColor = '#1B5E20';
      const allCoords: Array<{ latitude: number; longitude: number }> = [];
      for (let i = 0; i < selectedDayItineraryPinsOrdered.length - 1; i++) {
        if (cancelled) return;
        const a = selectedDayItineraryPinsOrdered[i];
        const b = selectedDayItineraryPinsOrdered[i + 1];
        const start = { latitude: a.coordinates.latitude, longitude: a.coordinates.longitude };
        const end = { latitude: b.coordinates.latitude, longitude: b.coordinates.longitude };
        try {
          const route = await calculateDetailedRoute(start, end, preferredTravelMode);
          if (route?.geometry?.length) {
            // Append geometry, skipping duplicate point at joins.
            route.geometry.forEach((c, idx) => {
              const prev = allCoords[allCoords.length - 1];
              if (idx === 0 && prev && prev.latitude === c.latitude && prev.longitude === c.longitude) return;
              allCoords.push({ latitude: c.latitude, longitude: c.longitude });
            });
          } else {
            // Fallback to straight segment
            if (allCoords.length === 0) allCoords.push(start);
            allCoords.push(end);
          }
        } catch {
          if (allCoords.length === 0) allCoords.push(start);
          allCoords.push(end);
        }
      }

      const lines: RouteLine[] =
        allCoords.length >= 2
          ? [
            {
              id: `day-seq-${selectedTrip.id}-${dayKey}`,
              coordinates: allCoords,
              color: routeColor,
              width: 3,
            },
          ]
          : [];

      if (!cancelled) {
        daySequenceCacheRef.current.set(cacheKey, lines);
        setDaySequenceRoutes(lines);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [enableDaySequenceRoute, preferredTravelMode, selectedDay, selectedDayIndex, selectedDayItineraryPinsOrdered, selectedTrip?.id]);

  const computeBearingDeg = useCallback((from: { latitude: number; longitude: number }, to: { latitude: number; longitude: number }) => {
    const toRad = (d: number) => (d * Math.PI) / 180;
    const toDeg = (r: number) => (r * 180) / Math.PI;
    const lat1 = toRad(from.latitude);
    const lat2 = toRad(to.latitude);
    const dLon = toRad(to.longitude - from.longitude);
    const y = Math.sin(dLon) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
    const brng = (toDeg(Math.atan2(y, x)) + 360) % 360;
    return brng;
  }, []);

  const distanceMeters = useCallback((a: { latitude: number; longitude: number }, b: { latitude: number; longitude: number }) => {
    const R = 6371000;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad(b.latitude - a.latitude);
    const dLon = toRad(b.longitude - a.longitude);
    const lat1 = toRad(a.latitude);
    const lat2 = toRad(b.latitude);
    const s1 = Math.sin(dLat / 2);
    const s2 = Math.sin(dLon / 2);
    const h = s1 * s1 + Math.cos(lat1) * Math.cos(lat2) * s2 * s2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
  }, []);

  // Build arrowheads along the selected day's routed polyline (small + capped for performance).
  useEffect(() => {
    let cancelled = false;
    const build = async () => {
      if (!enableDaySequenceRoute) {
        setDaySequenceArrows([]);
        return;
      }
      if (selectedDayIndex < 0 || daySequenceRoutes.length === 0) {
        setDaySequenceArrows([]);
        return;
      }
      const route = daySequenceRoutes[0];
      const coords = route?.coordinates || [];
      if (coords.length < 2) {
        setDaySequenceArrows([]);
        return;
      }
      const spacing =
        preferredTravelMode === 'walking' ? 450 : preferredTravelMode === 'transit' ? 650 : 800;
      const maxArrows = 12;
      const arrows: RouteArrow[] = [];
      let acc = 0;
      for (let i = 0; i < coords.length - 1; i++) {
        if (cancelled) return;
        const a = coords[i];
        const b = coords[i + 1];
        const seg = distanceMeters(a, b);
        acc += seg;
        if (acc < spacing) continue;
        acc = 0;
        const bearing = computeBearingDeg(a, b);
        // Ionicons caret-up points up; rotate by bearing.
        arrows.push({
          id: `${route.id}-arrow-${i}`,
          coordinate: { latitude: b.latitude, longitude: b.longitude },
          bearingDeg: bearing,
          color: route.color || '#1B5E20',
          size: 14,
        });
        if (arrows.length >= maxArrows) break;
      }
      if (!cancelled) setDaySequenceArrows(arrows);
    };
    void build();
    return () => {
      cancelled = true;
    };
  }, [enableDaySequenceRoute, computeBearingDeg, daySequenceRoutes, distanceMeters, preferredTravelMode, selectedDayIndex]);

  // Best-effort weather for map cards: fetch a 5-day forecast for the primary destination.
  useEffect(() => {
    let cancelled = false;
    const loadWeather = async () => {
      if (!selectedTrip?.id || !selectedTrip.startDate || !selectedTrip.destination) {
        if (!cancelled) setMapDailyWeather({});
        if (!cancelled) {
          setWeatherStatus('idle');
          setWeatherError(null);
        }
        return;
      }
      try {
        if (!cancelled) {
          setWeatherStatus('loading');
          setWeatherError(null);
        }
        const start = selectedTrip.startDate instanceof Date ? selectedTrip.startDate : new Date(selectedTrip.startDate);
        const dest = (() => {
          const firstStop = selectedTrip.destinations?.[0];
          if (firstStop?.city) {
            return firstStop.country ? `${firstStop.city}, ${firstStop.country}` : firstStop.city;
          }
          // Keep full destination string (often includes country) to improve geocode accuracy.
          return (selectedTrip.destination || '').trim();
        })();
        const coords = dest ? await getWeatherDestinationCoordinates(dest) : null;
        if (!coords) {
          if (!cancelled) setMapDailyWeather({});
          if (!cancelled) {
            setWeatherStatus('error');
            setWeatherError('Unable to locate destination for weather.');
          }
          return;
        }

        const forecast = await fetchWeatherForecast({
          latitude: coords.latitude,
          longitude: coords.longitude,
          city: coords.city,
          countryCode: coords.country || undefined,
          days: 5,
          targetDate: toLocalDateKey(start),
        });
        if (!forecast) {
          if (!cancelled) setMapDailyWeather({});
          if (!cancelled) {
            setWeatherStatus('error');
            setWeatherError('Weather forecast unavailable. Check weather Cloud Function setup.');
          }
          return;
        }

        const mapCond = (c: string): DayWeather['condition'] => {
          const s = (c || '').toLowerCase();
          if (s.includes('snow')) return 'snowy';
          if (s.includes('thunder') || s.includes('storm')) return 'stormy';
          if (s.includes('rain') || s.includes('drizzle')) return 'rainy';
          if (s.includes('wind')) return 'windy';
          if (s.includes('cloud') && s.includes('part')) return 'partly_cloudy';
          if (s.includes('cloud')) return 'cloudy';
          if (s.includes('clear') || s.includes('sun')) return 'sunny';
          return 'partly_cloudy';
        };

        const next: Record<string, DayWeather> = {};
        forecast?.forecast?.forEach((d) => {
          const key = (d.date || '').slice(0, 10);
          if (!key) return;
          next[key] = {
            condition: mapCond(d.condition),
            tempHigh: Math.round(d.temperature.max),
            tempLow: Math.round(d.temperature.min),
            icon: d.icon,
          };
        });

        if (!cancelled) setMapDailyWeather(next);
        if (!cancelled) {
          setWeatherStatus('ready');
          setWeatherError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setMapDailyWeather({});
          setWeatherStatus('error');
          setWeatherError(
            (err as any)?.message
              ? `Weather fetch failed: ${(err as any)?.message}`
              : 'Weather fetch failed. Verify configuration and network.',
          );
          if (__DEV__) {
            console.warn('[TripMapExperience] Weather fetch failed', err);
          }
        }
      }
    };

    void loadWeather();
    return () => {
      cancelled = true;
    };
  }, [selectedTrip?.id, selectedTrip?.destination, selectedTrip?.destinations, selectedTrip?.startDate, weatherReloadNonce]);

  // Day-specific pins for summaries/metrics
  const dayPinsForSummary = useMemo(() => {
    if (!selectedDay) return mapPinsForRender || [];
    const selectedDayKey = toDateKey(selectedDay.date);
    return mapPinsForRender.filter((pin) => {
      if (pin.type !== 'itinerary') return true;
      if (!selectedDayKey) return true;
      const pinKey = pin.dateKey || toDateKey((pin.data as ItineraryItem | undefined)?.startDate);
      // Pins with missing day metadata shouldn't appear in a specific day summary.
      if (!pinKey) return false;
      return pinKey === selectedDayKey;
    });
  }, [mapPinsForRender, selectedDay]);

  const selectedDayPinsOnMap = selectedDayItineraryPins.length;
  const selectedDayActivityCount = selectedDayActivities?.length ?? 0;

  const hasLocationText = useCallback((item: ItineraryItem): boolean => {
    const raw = `${item.address || item.location || ''}`.trim();
    return raw.length > 0;
  }, []);

  const missingLocationItems = useMemo(() => {
    return (itineraryItems || []).filter((item) => !hasLocationText(item));
  }, [hasLocationText, itineraryItems]);

  const missingLocationsByDay = useMemo<Record<string, number>>(() => {
    const out: Record<string, number> = {};
    missingLocationItems.forEach((item) => {
      const key = toDateKey(item.startDate);
      if (!key) return;
      out[key] = (out[key] || 0) + 1;
    });
    return out;
  }, [missingLocationItems]);

  const selectedDayMissingLocations = useMemo(() => {
    if (!selectedDay) return 0;
    const key = toDateKey(selectedDay.date);
    if (!key) return 0;
    return missingLocationsByDay[key] || 0;
  }, [missingLocationsByDay, selectedDay]);

  const itineraryPinsPinnedCount = useMemo(() => {
    return (mapPins || []).filter((p) => p.type === 'itinerary').length;
  }, [mapPins]);

  const itineraryItemsWithLocationsCount = useMemo(() => {
    return (itineraryItems || []).filter(hasLocationText).length;
  }, [hasLocationText, itineraryItems]);

  // If the user changes the selected day while geocoding is still running, reprioritize that day.
  // IMPORTANT: Do not call refreshPins when only `pinsLoading` / `isGeocoding` toggles for the same
  // day — that was restarting pinning from scratch on every load (2–3 full cycles + pin blink).
  const lastPriorityTripIdRef = useRef<string | null>(null);
  const prevSelectedDayKeyForGeocodePriorityRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!selectedTripId) {
      lastPriorityTripIdRef.current = null;
      prevSelectedDayKeyForGeocodePriorityRef.current = undefined;
      return;
    }

    if (lastPriorityTripIdRef.current !== selectedTripId) {
      lastPriorityTripIdRef.current = selectedTripId;
      prevSelectedDayKeyForGeocodePriorityRef.current = undefined;
    }

    const nextKey = selectedDay ? toDateKey(selectedDay.date) : null;

    if (prevSelectedDayKeyForGeocodePriorityRef.current === undefined) {
      prevSelectedDayKeyForGeocodePriorityRef.current = nextKey;
      return;
    }

    const prevKey = prevSelectedDayKeyForGeocodePriorityRef.current;
    const dayChanged = prevKey !== nextKey;
    prevSelectedDayKeyForGeocodePriorityRef.current = nextKey;

    if (!dayChanged) return;
    if (!pinsLoading && !isGeocoding) return;

    refreshPins(nextKey);
  }, [selectedDay, selectedTripId, pinsLoading, isGeocoding, refreshPins]);

  // Synthetic "All days" object so the summary card can render even when `selectedDay` is null
  // (the All tab uses `selectedDayIndex = -1`).
  const allDaysTripDay = useMemo<TripDay>(() => {
    const count = (itineraryItems || []).length;
    return {
      date: new Date(),
      dayNumber: -1,
      label: 'All',
      dateLabel: 'All days',
      isToday: false,
      isPast: false,
      isFuture: false,
      activityCount: count,
      hasActivities: count > 0,
    };
  }, [itineraryItems]);

  const renderMissingPinsNotice = (embedded: boolean = true) => {
    if (!selectedDay || selectedDayActivityCount === 0) return null;
    const hasMissing = selectedDayMissingLocations > 0 || selectedDayPinsOnMap < selectedDayActivityCount;
    if (!hasMissing) return null;
    const missingLabel = selectedDayMissingLocations > 0
      ? `${selectedDayMissingLocations} activit${selectedDayMissingLocations === 1 ? 'y' : 'ies'} missing locations`
      : null;
    return (
      <View style={embedded ? [styles.missingPinsNotice, styles.missingPinsNoticeEmbedded] : styles.missingPinsNotice}>
        <View style={styles.missingPinsText}>
          {missingLabel ? (
            <Caption color={theme.colors.warning || theme.colors.error} numberOfLines={1}>
              {missingLabel}
            </Caption>
          ) : null}
          <Caption color={theme.colors.textSecondary} numberOfLines={2}>
            {selectedDayPinsOnMap === 0
              ? 'Add locations to your activities to see pins on the map.'
              : `Pins on map: ${selectedDayPinsOnMap}/${selectedDayActivityCount}`}
          </Caption>
        </View>
        <Button
          label="Add locations"
          size="small"
          variant="secondary"
          onPress={() => {
            if (selectedTripId) {
              router.push(`/(tabs)/plan?tripId=${selectedTripId}&from=maps&focus=locations`);
            } else {
              router.push('/(tabs)/plan?from=maps&focus=locations');
            }
          }}
        />
      </View>
    );
  };

  // Map bounds for the currently selected day (use exactly the itinerary pins visible for that day).
  const selectedDayPinsForViewport = useMemo(() => {
    if (!selectedDay || selectedDayIndex < 0) return [] as MapPin[];
    const selectedDayKey = toDateKey(selectedDay.date);
    if (!selectedDayKey) return [] as MapPin[];
    return pinsWithDayIndexes.filter((pin) => {
      if (pin.type !== 'itinerary') return false;
      const pinKey = pin.dateKey || toDateKey((pin.data as ItineraryItem | undefined)?.startDate);
      if (!pinKey) return false;
      return pinKey === selectedDayKey;
    });
  }, [pinsWithDayIndexes, selectedDay, selectedDayIndex]);

  // City / trip anchor for map auto-framing (destination, not scattered bad geocodes).
  const tripMapFramingAnchor = useMemo(() => {
    const fromPair = (lat?: number, lng?: number) => {
      if (lat == null || lng == null) return null;
      return isUsableLatLng(lat, lng) ? { latitude: lat, longitude: lng } : null;
    };
    const pr = primaryReferencePoint?.coordinates;
    if (pr) {
      const c = fromPair(pr.latitude, pr.longitude);
      if (c) return c;
    }
    if (isMultiCityTrip && selectedCityDestination?.coordinates) {
      const d = selectedCityDestination.coordinates;
      const c = fromPair(d.latitude, d.longitude);
      if (c) return c;
    }
    const first = selectedTrip?.destinations?.[0]?.coordinates;
    if (first) {
      const c = fromPair(first.latitude, first.longitude);
      if (c) return c;
    }
    if (mapCenter) {
      const c = fromPair(mapCenter.latitude, mapCenter.longitude);
      if (c) return c;
    }
    return null;
  }, [
    primaryReferencePoint?.coordinates,
    isMultiCityTrip,
    selectedCityDestination,
    selectedTrip?.destinations,
    mapCenter,
  ]);

  // Map bounds for the currently selected day.
  const dayBounds = useMemo<MapBounds | null>(() => {
    if (!selectedDay || selectedDayIndex < 0) return null;
    if (selectedDayPinsForViewport.length === 0) return null;

    const dayAnchor = (() => {
      if (typeof selectedDay.cityIndex === 'number' && tripDestinations[selectedDay.cityIndex]?.coordinates) {
        const c = tripDestinations[selectedDay.cityIndex]!.coordinates!;
        if (isUsableLatLng(c.latitude, c.longitude)) {
          return { latitude: c.latitude, longitude: c.longitude };
        }
      }
      const destInfo = findDestinationForDate(selectedDay.date);
      const c = destInfo?.destination?.coordinates;
      if (c && isUsableLatLng(c.latitude, c.longitude)) {
        return { latitude: c.latitude, longitude: c.longitude };
      }
      return tripMapFramingAnchor;
    })();

    let coords = selectedDayPinsForViewport
      .map((p) => p.coordinates)
      .filter(
        (c) =>
          c &&
          Number.isFinite(c.latitude) &&
          Number.isFinite(c.longitude) &&
          !(c.latitude === 0 && c.longitude === 0) &&
          Math.abs(c.latitude) <= 90 &&
          Math.abs(c.longitude) <= 180,
      ) as { latitude: number; longitude: number }[];

    if (dayAnchor && coords.length >= 2) {
      const near = coords.filter(
        (c) => distanceKmToAnchor(dayAnchor, c.latitude, c.longitude) <= MAX_MAP_AUTO_FRAME_KM,
      );
      if (near.length >= 2) {
        coords = near;
      } else if (near.length === 1) {
        coords = near;
      } else {
        coords = [];
      }
    }

    if (coords.length === 0) return null;
    let minLat = coords[0].latitude;
    let maxLat = coords[0].latitude;
    let minLng = coords[0].longitude;
    let maxLng = coords[0].longitude;

    for (const c of coords) {
      if (c.latitude < minLat) minLat = c.latitude;
      if (c.latitude > maxLat) maxLat = c.latitude;
      if (c.longitude < minLng) minLng = c.longitude;
      if (c.longitude > maxLng) maxLng = c.longitude;
    }

    // Keep day views at a city-readable scale.
    // Very tight fits can feel "blank" and hide map context, especially with overlays.
    const centerLat = (minLat + maxLat) / 2;
    const latSpan = Math.abs(maxLat - minLat);
    const lngSpan = Math.abs(maxLng - minLng);
    const minLatSpan = Platform.OS === 'android' ? 0.02 : 0.018; // roughly 2.0-2.2km
    const minLngSpan = Math.max(minLatSpan * Math.abs(Math.cos(centerLat * Math.PI / 180)), minLatSpan * 0.7);
    if (latSpan < minLatSpan) {
      const delta = (minLatSpan - latSpan) / 2;
      minLat -= delta;
      maxLat += delta;
    }
    if (lngSpan < minLngSpan) {
      const delta = (minLngSpan - lngSpan) / 2;
      minLng -= delta;
      maxLng += delta;
    }

    // If only one pin or extremely tight, pad slightly.
    // Keep this tighter so single-activity days still feel city-focused.
    if (coords.length === 1 || (Math.abs(maxLat - minLat) < 0.0005 && Math.abs(maxLng - minLng) < 0.0005)) {
      const padding = 0.006;
      minLat -= padding;
      maxLat += padding;
      minLng -= padding;
      maxLng += padding;
    }

    return {
      southWest: { latitude: minLat, longitude: minLng },
      northEast: { latitude: maxLat, longitude: maxLng },
    };
  }, [
    selectedDayPinsForViewport,
    selectedDay,
    selectedDayIndex,
    recenterNonce,
    tripDestinations,
    findDestinationForDate,
    tripMapFramingAnchor,
  ]);

  // Default map framing: fit to city-level pins (not the whole world).
  // - For multi-city trips (when not in “whole trip” mode), bound to the selected city’s dates.
  // - If pins are spread too far (bad geocodes or multi-city), fall back to `mapCenter` + zoomLevel.
  const initialMapBounds = useMemo(() => {
    if (!showFullTripView && dayBounds) {
      return dayBounds;
    }

    const pinsForBounds: MapPin[] = (() => {
      if (showFullTripView) {
        return pinsWithDayIndexes;
      }

      // Multi-city: only include itinerary pins that fall within the selected city's date range.
      if (isMultiCityTrip && selectedCityDestination) {
        const start = new Date(selectedCityDestination.startDate);
        const end = new Date(selectedCityDestination.endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);

        const itineraryPinsInRange = pinsWithDayIndexes.filter((pin) => {
          if (pin.type !== 'itinerary') return false;
          // If the pin has an explicit cityIndex (best signal), use it to prevent overlap-day bleed.
          if (pin.cityIndex !== undefined && pin.cityIndex !== null) {
            return pin.cityIndex === selectedCityIndex;
          }
          const item = pin.data as ItineraryItem | undefined;
          if (!item?.startDate) return false;
          const d = new Date(item.startDate);
          return d >= start && d <= end;
        });

        const cityScopedPins = pinsWithDayIndexes.filter((pin) => {
          if (pin.cityIndex !== undefined && pin.cityIndex !== null) {
            return pin.cityIndex === selectedCityIndex;
          }
          if (pin.type !== 'itinerary') return false;
          const item = pin.data as ItineraryItem | undefined;
          if (!item?.startDate) return false;
          const d = new Date(item.startDate);
          return d >= start && d <= end;
        });

        // Keep viewport scoped to the active city whenever possible.
        // Falling back to every trip pin can yank the map to unrelated cities because of one bad/outlier pin.
        if (cityScopedPins.length > 0) {
          return cityScopedPins;
        }
        if (itineraryPinsInRange.length > 0) {
          return itineraryPinsInRange;
        }
        return pinsWithDayIndexes;
      }

      return pinsWithDayIndexes;
    })();

    // "All days" overview: auto-fit to itinerary stops by default (keeps the first frame tight).
    // When discovery landmarks anchor a different metro (baseline/live city vs where pins geocoded),
    // include those landmarks and allow a wider bounds pass so they are not off-screen.
    let includeLandmarksForTripOverview = false;
    let viewportPinsForBounds: MapPin[] = pinsForBounds;
    if (selectedDayIndex < 0 && !showFullTripView) {
      const itineraryPins = pinsForBounds.filter((p) => p.type === 'itinerary');
      const itinWithCoords = itineraryPins.filter((p) => {
        const c = p.coordinates;
        return (
          c &&
          Number.isFinite(c.latitude) &&
          Number.isFinite(c.longitude) &&
          !(c.latitude === 0 && c.longitude === 0) &&
          Math.abs(c.latitude) <= 90 &&
          Math.abs(c.longitude) <= 180
        );
      });
      if (itinWithCoords.length >= 2) {
        const landmarkPins = pinsForBounds.filter((p) => p.type === 'landmark');
        const prox = landmarkFetchProximity;
        const sumLatLng = itinWithCoords.reduce(
          (acc, p) => ({
            lat: acc.lat + p.coordinates.latitude,
            lng: acc.lng + p.coordinates.longitude,
          }),
          { lat: 0, lng: 0 },
        );
        const itinCenter = {
          latitude: sumLatLng.lat / itinWithCoords.length,
          longitude: sumLatLng.lng / itinWithCoords.length,
        };
        const splitRegions =
          landmarkPins.length > 0 &&
          prox &&
          isUsableLatLng(prox.latitude, prox.longitude) &&
          haversineDistance(
            { lat: itinCenter.latitude, lng: itinCenter.longitude },
            { lat: prox.latitude, lng: prox.longitude },
          ) >= LANDMARK_TRIP_FIT_SPLIT_MIN_KM;
        includeLandmarksForTripOverview = Boolean(splitRegions);
        viewportPinsForBounds = splitRegions ? [...itineraryPins, ...landmarkPins] : itineraryPins;
      } else if (itinWithCoords.length === 1) {
        // Single Stop / single geocoded day: still need a real fitBounds — otherwise the camera
        // stays on the default city zoom and pins look "missing" until the user zooms.
        viewportPinsForBounds = itineraryPins;
        includeLandmarksForTripOverview = false;
      } else {
        return null;
      }
    }

    const pinsFiltered = (() => {
      // Exclude user/search pins from auto-fit. Including them can zoom out wildly
      // (e.g., user location far away, or a search result in another country).
      const withoutUserAndSearch = viewportPinsForBounds.filter((p) => p.type !== 'user' && p.type !== 'search');

      // Strong preference: fit to itinerary + landmarks (not a single “destination fallback” pin).
      const withoutFallbackDestination = withoutUserAndSearch.filter((p) => p.id !== 'destination-fallback');

      // If we still have < 2, allow fallback to anything we have (better than null).
      const candidate = withoutFallbackDestination.length >= 2 ? withoutFallbackDestination : withoutUserAndSearch;

      return candidate.length >= 2 ? candidate : viewportPinsForBounds;
    })();

    const pinsForAutoFrame = (() => {
      if (showFullTripView || !tripMapFramingAnchor) {
        return pinsFiltered;
      }
      if (includeLandmarksForTripOverview) {
        return pinsFiltered;
      }
      const anchor = tripMapFramingAnchor;
      const near = pinsFiltered.filter((p) => {
        const c = p.coordinates;
        if (!c || !isUsableLatLng(c.latitude, c.longitude)) return false;
        return distanceKmToAnchor(anchor, c.latitude, c.longitude) <= MAX_MAP_AUTO_FRAME_KM;
      });
      if (near.length >= 2) return near;
      if (near.length === 1) return near;
      if (near.length === 0 && pinsFiltered.length >= 2) {
        return [] as MapPin[];
      }
      return pinsFiltered;
    })();

    const pts = pinsForAutoFrame
      .map((p) => p.coordinates)
      .filter((c) => c && !(c.latitude === 0 && c.longitude === 0));
    if (pts.length === 0) return null;

    let minLat = pts[0].latitude;
    let maxLat = pts[0].latitude;
    let minLng = pts[0].longitude;
    let maxLng = pts[0].longitude;

    for (const p of pts) {
      if (p.latitude < minLat) minLat = p.latitude;
      if (p.latitude > maxLat) maxLat = p.latitude;
      if (p.longitude < minLng) minLng = p.longitude;
      if (p.longitude > maxLng) maxLng = p.longitude;
    }

    // Detect if pins are clustered (very close together)
    const latRange = Math.abs(maxLat - minLat);
    const lngRange = Math.abs(maxLng - minLng);
    const isClustered = latRange < 0.001 && lngRange < 0.001; // ~100m range

    // Calculate appropriate padding based on clustering
    // For clustered pins, use larger padding to ensure all pins are visible
    // For spread out pins, use smaller padding
    let latPadding: number;
    let lngPadding: number;

    if (isClustered) {
      // When pins are clustered, calculate padding based on number of pins
      // More pins = more padding needed to show them all
      const pinCount = pinsForAutoFrame.length;
      // Base padding increases with pin count (in degrees)
      // ~0.001 degrees ≈ 111m, so 0.005 ≈ 555m
      const basePadding = Math.min(0.005, 0.001 * Math.sqrt(pinCount));
      latPadding = Math.max(basePadding, 0.002); // At least 0.002 degrees (~222m)
      lngPadding = Math.max(basePadding * Math.abs(Math.cos(minLat * Math.PI / 180)), 0.002);
    } else {
      // For spread out pins, use proportional padding (5% of range)
      latPadding = Math.max(latRange * 0.05, 0.001);
      lngPadding = Math.max(lngRange * 0.05, 0.001);
    }

    // Apply padding
    minLat -= latPadding;
    maxLat += latPadding;
    minLng -= lngPadding;
    maxLng += lngPadding;

    // If the diagonal is huge, don't fit bounds (it will zoom out to a globe).
    // Fall back to the trip/city center with the normal zoomLevel.
    try {
      const diagonalKm = haversineDistance(
        { lat: minLat, lng: minLng },
        { lat: maxLat, lng: maxLng },
      );
      // Country / multi-leg day trips: allow a wider diagonal when framing itinerary + distant discovery pins.
      const maxDiagonalKm = showFullTripView
        ? 6000
        : includeLandmarksForTripOverview
          ? 950
          : 260;
      if (diagonalKm > maxDiagonalKm) {
        return null;
      }
    } catch {
      // ignore; proceed with bounds
    }

    return {
      southWest: { latitude: minLat, longitude: minLng },
      northEast: { latitude: maxLat, longitude: maxLng },
    };
  }, [
    dayBounds,
    isMultiCityTrip,
    pinsWithDayIndexes,
    selectedCityDestination,
    selectedDayIndex,
    showFullTripView,
    recenterNonce,
    tripMapFramingAnchor,
    landmarkFetchProximity,
  ]);

  // Active bounds passed to the map (disable when a callout is open to avoid unwanted panning)
  const mapBounds = useMemo(() => {
    if (calloutPinId) return null;
    return showFullTripView
      ? fullTripBounds ?? null
      : !userHasInteractedWithMap
        ? initialMapBounds
        : null;
  }, [calloutPinId, fullTripBounds, initialMapBounds, showFullTripView, userHasInteractedWithMap, recenterNonce, selectedDayIndex]);

  // Once we have bounds to apply, clear the "pending auto-fit" flag.
  // (We keep ignoring zoom-change events briefly to avoid treating programmatic fitBounds as a user gesture.)
  useEffect(() => {
    if (!pendingAutoFitRef.current) return;
    if (!mapBounds) return;
    ignoreZoomEventsUntilRef.current = Date.now() + 1200;
    pendingAutoFitRef.current = false;
  }, [mapBounds]);

  // If bounds aren't available (only 1 pin, etc.), fall back to the next-activity pin.
  const nextActivityPin = useMemo(() => {
    const nextId = navigationPrompt?.nextActivity?.id;
    if (!nextId) return null;
    const byDataId = mapPinsForRender.find((p) => {
      if (p.type !== 'itinerary') return false;
      const item = p.data as ItineraryItem | undefined;
      return item?.id === nextId;
    });
    if (byDataId) return byDataId;
    // Fallback: our itinerary pins often have id `itinerary-${item.id}`
    return mapPinsForRender.find((p) => p.id === `itinerary-${nextId}`) ?? null;
  }, [mapPinsForRender, navigationPrompt?.nextActivity?.id]);

  const nextActivityPinForCentering = useMemo(() => {
    if (!nextActivityPin) return null;
    if (!tripMapFramingAnchor) return nextActivityPin;
    const c = nextActivityPin.coordinates;
    if (!isUsableLatLng(c.latitude, c.longitude)) return null;
    const km = distanceKmToAnchor(tripMapFramingAnchor, c.latitude, c.longitude);
    // Ignore outlier itinerary pins for initial centering.
    if (km > MAX_MAP_AUTO_FRAME_KM) return null;
    return nextActivityPin;
  }, [nextActivityPin, tripMapFramingAnchor]);

  const handlePinPress = useCallback((pin: MapPin) => {
    if (__DEV__) {
      console.log('[TripMapExperience] Pin pressed:', pin.title, pin.type);
    }
    // Use the main screen sheet (no inline map callout)
    setSelectedPin(pin);
    setShowPinPreview(Platform.OS !== 'web');
    setShowPinModal(Platform.OS === 'web');
    setCalloutPinId(pin.id);
  }, []);

  const openExternalDirections = useCallback((pin: MapPin, mode: 'driving' | 'walking' | 'transit') => {
    const url = buildExternalDirectionsUrl(pin, mode);
    if (url) {
      Linking.openURL(url).catch((err) => {
        console.error('[TripMap] Error opening maps:', err);
        Alert.alert('Unable to open maps', 'Please try again or open your maps app manually.');
      });
    }
  }, []);

  const handleGetDirectionsFromPin = useCallback(
    (pin: MapPin, modeOverride?: 'driving' | 'walking' | 'transit') => {
      const chosenMode = modeOverride ?? directionMode;

      if (__DEV__) {
        console.log('[TripMap] Getting directions to:', pin.title, pin.coordinates, 'mode:', chosenMode);
      }

      if (!buildExternalDirectionsUrl(pin, chosenMode)) {
        Alert.alert(
          'Invalid Destination',
          'Could not open directions. Add a street address to this activity or try again after the map refreshes.',
        );
        return;
      }
      openExternalDirections(pin, chosenMode);

      // Clear any existing in-app directions UI/lines
      setCalloutPinId(pin.id);
      setDirectionsDestination(null);
      setShowDirectionsPanel(false);
      setCurrentRoute(null);
      setCalculatedRoute(null);
    },
    [directionMode, openExternalDirections],
  );


  const handleAddToItinerary = useCallback((pin: MapPin) => {
    console.log('[TripMap] Add to Itinerary called for:', pin.title, pin.type);
    if (!selectedTrip) {
      console.warn('[TripMap] No trip selected');
      Alert.alert('No Trip Selected', 'Please select a trip first to add items to your itinerary.');
      return;
    }

    if (!firebaseUser?.uid) {
      console.warn('[TripMap] No user authenticated');
      Alert.alert('Authentication Required', 'Please sign in to add items to your itinerary.');
      return;
    }

    console.log('[TripMap] Setting landmark to add and showing form');
    setLandmarkToAdd(pin);
    setShowItineraryForm(true);
    setShowPinModal(false);
    console.log('[TripMap] Itinerary form should now be visible');
  }, [selectedTrip, firebaseUser?.uid]);

  const handleItineraryFormSubmit = useCallback(async (item: Omit<import('@/services/itinerary').ItineraryItem, 'id' | 'tripId' | 'createdAt' | 'updatedAt'>) => {
    if (!selectedTripId || !firebaseUser?.uid || !landmarkToAdd) {
      throw new Error('Missing required information');
    }

    try {
      const itemId = await createItineraryItem(selectedTripId, {
        ...item,
        createdBy: firebaseUser.uid,
        source: 'manual',
        status: 'planned',
      });

      setShowItineraryForm(false);
      setLandmarkToAdd(null);

      const offlineNote = !networkMonitor.isOnline()
        ? ' It will appear everywhere once it syncs when you are back online.'
        : '';
      Alert.alert(
        'Success',
        `${landmarkToAdd.title} has been added to your itinerary!${offlineNote}`,
        [
          {
            text: 'View Itinerary',
            onPress: () => {
              router.push(`/(tabs)/plan?tripId=${selectedTripId}&from=maps&focus=locations`);
            },
          },
          {
            text: 'OK',
            style: 'default',
          },
        ]
      );
    } catch (error) {
      console.error('[TripMap] Error adding item to itinerary:', error);
      Alert.alert('Error', error instanceof Error ? error.message : 'Failed to add item to itinerary. Please try again.');
      throw error;
    }
  }, [selectedTripId, firebaseUser?.uid, landmarkToAdd, router]);

  const handleCloseDirectionsPanel = useCallback(() => {
    setShowDirectionsPanel(false);
    setDirectionsDestination(null);
    setCurrentRoute(null);
    setCalculatedRoute(null);
  }, []);

  const handleOpenExternalMaps = useCallback(() => {
    if (!directionsDestination) return;
    openExternalDirections(directionsDestination, directionMode);
  }, [directionMode, directionsDestination, openExternalDirections]);

  const handleZoomIn = useCallback(() => {
    zoomCommandNonceRef.current += 1;
    setZoomAdjustment({ nonce: zoomCommandNonceRef.current, delta: 0.75 });
  }, []);

  const handleZoomOut = useCallback(() => {
    zoomCommandNonceRef.current += 1;
    setZoomAdjustment({ nonce: zoomCommandNonceRef.current, delta: -0.75 });
  }, []);

  const handleOptimizeItinerary = useCallback(() => {
    if (!selectedTrip || !itineraryItems || itineraryItems.length < 3) {
      Alert.alert(
        'Not Enough Items',
        'You need at least 3 itinerary items to optimize the route order.'
      );
      return;
    }
    setShowRouteOptimization(true);
  }, [selectedTrip, itineraryItems]);

  const handleFitDay = useCallback(() => {
    setUserHasInteractedWithMap(false);
    setCalloutPinId(null);
    setRecenterNonce((n) => n + 1);
  }, []);

  // Search box removed: users can use native maps for ad-hoc place search.

  const handleApplyOptimization = useCallback(async (optimizedOrder: ItineraryItem[]) => {
    if (!selectedTrip?.id) {
      Alert.alert('Unable to apply', 'Select a trip before applying route optimization.');
      return;
    }
    if (!Array.isArray(optimizedOrder) || optimizedOrder.length < 2) {
      Alert.alert('Unable to apply', 'Not enough optimized items were generated.');
      return;
    }

    const itineraryById = new Map(itineraryItems.map((item) => [item.id, item]));
    const orderedItems = optimizedOrder
      .map((item) => itineraryById.get(item.id))
      .filter((item): item is ItineraryItem => Boolean(item));

    if (orderedItems.length < 2) {
      Alert.alert('Unable to apply', 'We could not match optimized items to your itinerary.');
      return;
    }

    const targetDayKey = toLocalDateKey(orderedItems[0].startDate);
    const sameDayOptimizedItems = orderedItems.filter(
      (item) => toLocalDateKey(item.startDate) === targetDayKey,
    );

    if (sameDayOptimizedItems.length < 2) {
      Alert.alert('Unable to apply', 'Optimization only supports reordering items from a single day.');
      return;
    }

    const existingTimeSlots = sameDayOptimizedItems
      .map((item) => parseClockToMinutes(item.startTime))
      .filter((value): value is number => value !== null)
      .sort((a, b) => a - b);

    const slotCount = sameDayOptimizedItems.length;
    const appliedSlots: number[] = [];
    if (existingTimeSlots.length > 0) {
      appliedSlots.push(...existingTimeSlots.slice(0, slotCount));
      while (appliedSlots.length < slotCount) {
        const previous = appliedSlots[appliedSlots.length - 1] ?? (9 * 60);
        appliedSlots.push(Math.min(previous + 30, 23 * 60 + 59));
      }
    } else {
      for (let index = 0; index < slotCount; index += 1) {
        appliedSlots.push(Math.min(9 * 60 + index * 30, 23 * 60 + 59));
      }
    }

    const updates = sameDayOptimizedItems.map((item, index) =>
      updateItineraryItem(selectedTrip.id, item.id, {
        startTime: formatMinutesToClock(appliedSlots[index]),
      }),
    );
    const results = await Promise.allSettled(updates);
    const failed = results.filter((result) => result.status === 'rejected');
    if (failed.length > 0) {
      throw new Error(`Failed to update ${failed.length} itinerary item(s).`);
    }

    if (existingTimeSlots.length === 0) {
      Alert.alert(
        'Optimization applied',
        'Activities were reordered and assigned suggested times. You can fine-tune times in Plan.',
      );
    }

    router.push({
      pathname: '/(tabs)/plan',
      params: {
        tripId: selectedTrip.id,
      },
    });
  }, [selectedTrip, itineraryItems, router]);

  // Force inline callouts and disable bottom sheet so pins only show top card
  const shouldUseSheet = false;
  const clampMinutes = (value?: number | null) => {
    if (value === undefined || value === null || Number.isNaN(value)) return null;
    return Math.max(0, Math.round(value));
  };
  const formatDurationLabel = (minutes: number | null | undefined) => {
    if (minutes === null || minutes === undefined) return null;
    if (minutes >= 1440) {
      const days = Math.floor(minutes / 1440);
      const hours = Math.round((minutes % 1440) / 60);
      return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
    }
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = Math.round(minutes % 60);
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${minutes} min`;
  };

  const renderSheetContent = useCallback(() => {
    const pin = selectedPin || directionsDestination;
    const leaveMinutes = clampMinutes(navigationPrompt?.leaveInMinutes);
    const travelMinutes = clampMinutes(
      navigationPrompt?.travelDurationMinutes ??
      (navigationPrompt?.routeOptions?.[0]?.duration
        ? navigationPrompt.routeOptions[0].duration / 60
        : undefined) ??
      currentRoute?.duration ??
      pin?.distanceMeta?.referenceDistances?.[0]?.duration,
    );
    const arrivalLabel = navigationPrompt?.arrivalEta
      ? new Date(navigationPrompt.arrivalEta).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      : undefined;
    const leaveLabel = formatDurationLabel(leaveMinutes ?? 0);
    const travelLabel = formatDurationLabel(travelMinutes);
    const referenceLabel = primaryReferencePoint?.title || selectedTrip?.destination || 'trip center';

    const primaryCTA = () => {
      if (pin) {
        handleGetDirectionsFromPin(pin);
        return;
      }
      handleGetDirections();
      setShowDirectionsPanel(true);
    };

    const modeOptions: Array<{ id: 'driving' | 'walking' | 'both'; label: string }> = [
      { id: 'driving', label: 'Drive' },
      { id: 'walking', label: 'Walk' },
      { id: 'both', label: 'All' },
    ];

    return (
      <View style={[styles.sheetContent, { paddingBottom: insets.bottom + theme.spacing(2) }]}>
        {pin ? (
          <View style={{ gap: theme.spacing(0.5) }}>
            <ThemedText variant="h3" style={{ fontSize: 18, fontWeight: '700' }}>
              {pin.title}
            </ThemedText>
            {pin.subtitle ? (
              <Caption color={theme.colors.textSecondary}>{pin.subtitle}</Caption>
            ) : null}
            {travelLabel ? (
              <Caption color={theme.colors.textSecondary}>
                ~{travelLabel} from {referenceLabel} • {routeMode === 'walking' ? 'Walk' : routeMode === 'driving' ? 'Drive' : 'Mixed'}
              </Caption>
            ) : null}
          </View>
        ) : navigationPrompt ? (
          <View style={{ gap: theme.spacing(0.5) }}>
            <ThemedText variant="h3" style={{ fontSize: 18, fontWeight: '700' }}>
              Next Activity
            </ThemedText>
            <Body style={{ fontWeight: '600' }}>{navigationPrompt.nextActivity.title}</Body>
            <Caption color={theme.colors.textSecondary}>
              {leaveLabel ? `Leave in ${leaveLabel}` : 'Leave soon'}
              {travelLabel ? ` • ~${travelLabel} trip` : ''}
              {arrivalLabel ? ` • arrive around ${arrivalLabel}` : ''}
            </Caption>
            <Caption color={theme.colors.textSecondary}>
              Based on {referenceLabel || 'trip center'}
            </Caption>
            {navigationPrompt.message ? (
              <Caption color={theme.colors.textSecondary}>{navigationPrompt.message}</Caption>
            ) : null}
          </View>
        ) : (
          <View style={{ gap: theme.spacing(0.5) }}>
            <ThemedText variant="h3" style={{ fontSize: 18, fontWeight: '700' }}>
              Explore the map
            </ThemedText>
            <Caption color={theme.colors.textSecondary}>
              Select a pin to see details and directions.
            </Caption>
            {!currentLocation ? (
              <Caption color={theme.colors.textSecondary}>
                Enable location to see real-time travel times.
              </Caption>
            ) : null}
          </View>
        )}

        <View style={styles.modeSegment}>
          {modeOptions.map((option) => {
            const active = routeMode === option.id;
            return (
              <Pressable
                key={option.id}
                onPress={() => setRouteMode(option.id)}
                style={[
                  styles.modePill,
                  {
                    backgroundColor: active ? theme.colors.primary : theme.colors.surface,
                    borderColor: active ? theme.colors.primary : theme.colors.borderMuted,
                  },
                ]}
                accessibilityRole="button"
                accessibilityLabel={`${option.label} route mode`}
                accessibilityHint={active ? 'Current route mode' : 'Switch route mode'}
                accessibilityState={{ selected: active }}
              >
                <Caption
                  style={{
                    color: active ? theme.colors.surface : theme.colors.text,
                    fontWeight: '700',
                  }}
                >
                  {option.label}
                </Caption>
              </Pressable>
            );
          })}
        </View>

        <Button
          label={pin ? 'Get Directions' : 'Start directions'}
          onPress={primaryCTA}
          icon={<Ionicons name="navigate" size={18} color={theme.colorScheme === 'dark' ? '#000000' : '#FFFFFF'} />}
        />

        {pin && (pin.type === 'landmark' || pin.type === 'search') && (
          <Button
            label="Add to itinerary"
            variant="outlined"
            onPress={() => handleAddToItinerary(pin)}
            icon={<Ionicons name="add-circle-outline" size={18} color={theme.colors.primary} />}
          />
        )}

        {!pin && navigationPrompt?.routeOptions?.length ? (
          <View style={{ gap: theme.spacing(0.5) }}>
            <Caption style={{ fontWeight: '700' }}>Travel options</Caption>
            {navigationPrompt.routeOptions.slice(0, 3).map((opt, idx) => {
              const minutes = clampMinutes(opt.duration / 60) ?? 0;
              return (
                <Caption key={idx} color={theme.colors.textSecondary}>
                  {opt.mode === 'walking' ? 'Walk' : opt.mode === 'driving' ? 'Drive' : 'Transit'}: {minutes} min
                </Caption>
              );
            })}
          </View>
        ) : null}
      </View>
    );
  }, [
    currentRoute?.duration,
    directionsDestination,
    handleAddToItinerary,
    handleGetDirections,
    handleGetDirectionsFromPin,
    insets.bottom,
    navigationPrompt,
    routeMode,
    selectedPin,
    theme,
  ]);

  const renderPinCallout = useCallback(
    (pin: MapPin, onClose?: () => void) => {
      const formatStartTime = (time?: string): string | null => {
        if (!time || time === 'flexible') return time === 'flexible' ? 'Flexible' : null;
        const [hours, minutes] = time.split(':').map(Number);
        if (Number.isNaN(hours) || Number.isNaN(minutes)) return time;
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const hour12 = hours % 12 || 12;
        return `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
      };

      const itineraryItem =
        pin.type === 'itinerary' ? (pin.data as ItineraryItem | undefined) : undefined;
      const startTimeLabel = formatStartTime(itineraryItem?.startTime);

      const modeOptions: Array<{ id: 'driving' | 'walking' | 'transit'; label: string; icon: keyof typeof Ionicons.glyphMap }> = [
        { id: 'driving', label: 'Drive', icon: 'car-outline' },
        { id: 'walking', label: 'Walk', icon: 'walk' },
        { id: 'transit', label: 'Transit', icon: 'bus' },
      ];

      const activeMode = modeOptions.find((m) => m.id === directionMode) ?? modeOptions[0];

      return (
        <Card
          style={{
            padding: 12,
            borderRadius: 12,
            minWidth: 220,
            maxWidth: 280,
            backgroundColor: theme.colors.surface,
            borderColor: theme.colors.borderMuted,
            borderWidth: 1,
            gap: 10,
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <ThemedText numberOfLines={1} style={{ fontWeight: '700', fontSize: 13 }}>
                {pin.title}
              </ThemedText>
              {pin.subtitle ? (
                <Caption color={theme.colors.textSecondary} numberOfLines={1} style={{ fontSize: 10 }}>
                  {pin.subtitle}
                </Caption>
              ) : null}
              {pin.type === 'itinerary' && startTimeLabel ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 2 }}>
                  <Ionicons name="time-outline" size={12} color={theme.colors.textSecondary} />
                  <Caption color={theme.colors.textSecondary} numberOfLines={1} style={{ fontSize: 10 }}>
                    {startTimeLabel}
                  </Caption>
                </View>
              ) : null}
            </View>
            <Pressable
              onPress={onClose}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel="Close location quick actions"
              accessibilityHint="Closes this location card"
            >
              <Ionicons name="close" size={16} color={theme.colors.textSecondary} />
            </Pressable>
          </View>

          <View style={{ flexDirection: 'row', gap: 6, justifyContent: 'space-between' }}>
            {modeOptions.map((mode) => {
              const active = mode.id === directionMode;
              return (
                <Pressable
                  key={mode.id}
                  accessibilityRole="button"
                  accessibilityLabel={`${mode.label} directions mode`}
                  accessibilityHint={active ? 'Current directions mode' : 'Switch directions mode'}
                  accessibilityState={{ selected: active }}
                  onPress={() => setDirectionMode(mode.id)}
                  style={{
                    flex: 1,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 4,
                    paddingVertical: 7,
                    paddingHorizontal: 6,
                    borderRadius: 10,
                    borderWidth: 1,
                    borderColor: active ? theme.colors.primary : theme.colors.borderMuted,
                    backgroundColor: active ? theme.colors.primary + '12' : theme.colors.surface,
                  }}
                >
                  <Ionicons
                    name={mode.icon}
                    size={16}
                    color={active ? theme.colors.primary : theme.colors.textSecondary}
                  />
                  <Caption
                    style={{
                      fontWeight: active ? '700' : '600',
                      color: active ? theme.colors.primary : theme.colors.textSecondary,
                    }}
                  >
                    {mode.label}
                  </Caption>
                </Pressable>
              );
            })}
          </View>

          <View style={{ gap: 8 }}>
            <Button
              label="Get directions"
              size="small"
              onPress={() => {
                handleGetDirectionsFromPin(pin, directionMode);
                onClose?.();
              }}
              icon={<Ionicons name="navigate" size={14} color={theme.colorScheme === 'dark' ? '#000' : '#fff'} />}
              style={{ flex: 1 }}
            />
            {(pin.type === 'landmark' || pin.type === 'search') ? (
              <Button
                label="Add to itinerary"
                size="small"
                variant="outlined"
                onPress={() => {
                  handleAddToItinerary(pin);
                  onClose?.();
                }}
                icon={<Ionicons name="add" size={14} color={theme.colors.primary} />}
                style={{ flex: 1 }}
              />
            ) : null}
          </View>
        </Card>
      );
    },
    [directionMode, handleAddToItinerary, handleGetDirectionsFromPin, theme],
  );

  // Determine empty state - MUST be called before any early returns to maintain hook order
  const emptyStateType = useMemo(() => {
    if (!selectedTripId) return 'no-trip';
    // While pins are still loading/processing, avoid showing a blocking empty-state overlay.
    if (pinsLoading || itineraryItemsLoading) return null;
    if (selectedTrip && mapPins && mapPins.length === 0 && !isGeocoding) {
      if (pinCounts && pinCounts.itinerary === 0) return 'no-itinerary';
      return 'no-pins';
    }
    return null;
  }, [selectedTripId, pinsLoading, itineraryItemsLoading, selectedTrip, mapPins, isGeocoding, pinCounts]);

  useEffect(() => {
    if (!emptyStateType) setEmptyStateDismissed(false);
  }, [emptyStateType]);

  // Cached-map banner text (for offline sessions). Must stay above any early returns so
  // every render goes through the same hook sequence.
  const cachedMapsDescriptor = useMemo(() => {
    if (isOnlineNow) return null;
    if (tripLoadDebug?.lastUpdatedAt) {
      const d = new Date(tripLoadDebug.lastUpdatedAt);
      if (!Number.isNaN(d.getTime())) {
        return `Showing cached map data updated ${d.toLocaleString()}.`;
      }
    }
    return 'Showing cached map data while you are offline.';
  }, [isOnlineNow, tripLoadDebug?.lastUpdatedAt]);

  useEffect(() => {
    const unsubscribe = networkMonitor.addListener(() => {
      setIsOnlineNow(networkMonitor.isOnline());
    });
    return unsubscribe;
  }, []);

  // Layout constants and default zoom must run before any early returns to keep hook order stable
  // Summary overlay should be pinned to the top-left of the map (not pushed down).
  // It lives inside the map container, so it doesn't need to account for header/controls above.
  const viewportPlatform: ViewportPlatform =
    Platform.OS === 'android' ? 'android' : Platform.OS === 'web' ? 'web' : 'ios';
  const viewportSizeClass: ViewportSizeClass =
    screenHeight < 780 || screenWidth < 380 ? 'small' : screenHeight > 900 ? 'large' : 'regular';
  const viewportPreset = MAP_VIEWPORT_PRESETS[viewportPlatform][viewportSizeClass];
  const summaryOverlayTop = viewportPreset.summaryOverlayTop;
  // Place right controls closer to the bottom while staying above the tab bar/safe area.
  // Lift side FABs slightly on native so they sit higher (closer to prod / clear of bottom card).
  const nativeSideControlsLift = Platform.OS !== 'web' ? 24 : 0;
  const controlsBottomOffset =
    Math.max(
      viewportPreset.controlsBottomMin,
      insets.bottom + viewportPreset.controlsBottomInset,
    ) + nativeSideControlsLift;
  const recenterBottomOffset = Math.max(
    viewportPreset.recenterBottomMin,
    insets.bottom + viewportPreset.recenterBottomInset,
  );
  const miniSummaryWidth = Math.min(260, Math.max(200, Math.round(screenWidth * 0.58)));
  const useCompactWebToolsChip = Platform.OS === 'web' && screenWidth < 1120;

  const defaultZoomForView = useMemo(() => {
    const minZoom = 4;
    if (!userHasInteractedWithMap && !showFullTripView && !initialMapBounds && nextActivityPinForCentering) return Math.max(13, minZoom);
    if (!initialMapBounds && selectedDay) return Math.max(11, minZoom);
    if (selectedCityDestination || selectedTrip?.destination) return Math.max(12, minZoom);
    return minZoom;
  }, [userHasInteractedWithMap, showFullTripView, initialMapBounds, nextActivityPinForCentering, selectedDay, selectedCityDestination, selectedTrip?.destination]);

  // Initialize map zoom when we haven't interacted and no bounds are forcing a fit.
  useEffect(() => {
    if (userHasInteractedWithMap) return;
    if (initialMapBounds) return;
    setMapZoomLevel(defaultZoomForView);
  }, [defaultZoomForView, initialMapBounds, userHasInteractedWithMap]);

  const hasSheetContent = useMemo(
    () =>
      calloutPinId
        ? false
        : Boolean(selectedPin || directionsDestination || navigationPrompt),
    [selectedPin, directionsDestination, navigationPrompt, calloutPinId],
  );

  const mapBoundsPadding = useMemo(() => {
    const topOverlayHeight = viewportPreset.topOverlayHeight;
    const topBusyExtra = (pinsLoading || itineraryItemsLoading || isGeocoding) ? viewportPreset.topBusyExtra : 0;
    const allDaysExtra = selectedDayIndex < 0 ? viewportPreset.topAllDaysExtra : 0;
    const top = summaryOverlayTop + topOverlayHeight + topBusyExtra + allDaysExtra;

    const left = viewportPreset.sidePaddingLeft;
    const right = viewportPreset.sidePaddingRight;

    const bottomBase = Math.max(
      controlsBottomOffset + viewportPreset.bottomPaddingAdd,
      insets.bottom + viewportPreset.bottomPaddingMin,
    );
    const bottom = hasSheetContent ? bottomBase + viewportPreset.bottomSheetExtra : bottomBase;

    return { top, right, bottom, left };
  }, [
    controlsBottomOffset,
    hasSheetContent,
    insets.bottom,
    isGeocoding,
    itineraryItemsLoading,
    pinsLoading,
    selectedDayIndex,
    summaryOverlayTop,
    viewportPreset,
  ]);

  // When a prompt (or pin) exists, open the sheet to a readable height by default
  // so travel time text is visible without dragging.
  const initialSheetIndex = useMemo(() => {
    if (Platform.OS === 'web') return 0;
    if (navigationPrompt) return 1; // 160px snap
    if (selectedPin || directionsDestination) return 1; // 160px snap
    return 0;
  }, [navigationPrompt, selectedPin, directionsDestination]);

  // When directions are requested on native sheet, ensure the sheet opens to a readable height
  useEffect(() => {
    if (!shouldUseSheet || !renderNativeSheet) return;
    if (!directionsDestination) return;
    const ref = bottomSheetRef.current;
    if (ref?.snapToIndex) {
      try {
        // Use a taller snap when available (index 2 is '48%' on native)
        ref.snapToIndex(2);
      } catch (error) {
        // Fallback to mid snap if needed
        try {
          ref.snapToIndex(1);
        } catch {
          // ignore
        }
      }
    }
  }, [shouldUseSheet, renderNativeSheet, directionsDestination]);

  const notificationButton = (
    <View style={styles.headerActions}>
      <Pressable
        onPress={handleToggleNotifications}
        style={({ pressed }) => [
          styles.headerButton,
          pressed && { opacity: 0.7 },
        ]}
        accessibilityRole="button"
        accessibilityLabel={notificationsEnabled ? 'Disable map notifications' : 'Enable map notifications'}
        accessibilityHint="Toggles navigation and map alerts"
      >
        <Ionicons
          name={notificationsEnabled ? 'notifications' : 'notifications-outline'}
          size={24}
          color={notificationsEnabled ? theme.colors.primary : theme.colors.textSecondary}
        />
      </Pressable>
    </View>
  );

  // Loading state: avoid showing "No trips yet" while Firestore is still loading.
  // NOTE: All hooks must be called before early returns.
  if (tripsLoading) {
    return (
      <Screen
        title={hideHeader ? undefined : 'Maps'}
        subtitle={hideHeader ? undefined : 'View your itinerary and discover places'}
        scrollable={false}
        showOfflineIndicator={!hideHeader}
        disableTopInset={hideHeader}
      >
        <Card>
          <View style={{ alignItems: 'center', paddingVertical: theme.spacing(6), gap: theme.spacing(2) }}>
            {!loadingExtended ? (
              <>
                <ActivityIndicator size="large" />
                <Body color={theme.colors.textSecondary} style={{ textAlign: 'center', fontSize: 15, lineHeight: 22, maxWidth: 280 }}>
                  Loading trips...
                </Body>
                {showStagingDebug && (
                  <Pressable
                    onPress={handleCopyMapDebug}
                    style={({ pressed }) => [{ opacity: pressed ? 0.7 : 1 }]}
                    accessibilityLabel="Copy map debug info"
                    accessibilityHint="Copies internal diagnostics to clipboard"
                  >
                    <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center', maxWidth: 320 }}>
                      Tap to copy debug info
                    </Caption>
                  </Pressable>
                )}
              </>
            ) : (
              <View style={{ width: '100%' }}>
                <ErrorState
                  variant="inline"
                  title="Taking longer than expected"
                  message="This might be a connection issue."
                  onRetry={handleRetryLoad}
                  retryLabel="Retry"
                />
                {showStagingDebug && (
                  <Pressable
                    onPress={handleCopyMapDebug}
                    style={({ pressed }) => [{ opacity: pressed ? 0.7 : 1 }]}
                    accessibilityLabel="Copy map debug info"
                    accessibilityHint="Copies internal diagnostics to clipboard"
                  >
                    <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center', maxWidth: 320, marginTop: theme.spacing(1) }}>
                      Tap to copy debug info
                    </Caption>
                  </Pressable>
                )}
              </View>
            )}
          </View>
        </Card>
      </Screen>
    );
  }

  const didFailToLoadTrips =
    trips.length === 0 &&
    (tripLoadDebug?.lastSource === 'watchdog' ||
      !!tripLoadDebug?.subscriptionError ||
      !!tripLoadDebug?.fallbackError) &&
    tripLoadDebug?.cacheTripsLen === null &&
    tripLoadDebug?.subscriptionTripsLen === null &&
    tripLoadDebug?.fallbackTripsLen === null;

  // Early return for no trips - similar to Plan screen
  if (trips.length === 0) {
    return (
      <Screen
        title={hideHeader ? undefined : 'Maps'}
        subtitle={hideHeader ? undefined : 'View your itinerary and discover places'}
        scrollable={false}
        showOfflineIndicator={!hideHeader}
        disableTopInset={hideHeader}
      >
        <Card>
          <View style={{ alignItems: 'center', paddingVertical: theme.spacing(6), gap: theme.spacing(2) }}>
            {didFailToLoadTrips ? (
              <View style={{ width: '100%' }}>
                <ErrorState
                  variant="inline"
                  title="Trips failed to load"
                  message="We couldn’t load your trips for Maps."
                  onRetry={handleRetryLoad}
                  retryLabel="Retry"
                  details={showStagingDebug ? 'Tip: tap below to copy debug info.' : undefined}
                />
                {showStagingDebug && (
                  <Pressable
                    onPress={handleCopyMapDebug}
                    style={({ pressed }) => [{ opacity: pressed ? 0.7 : 1 }]}
                    accessibilityLabel="Copy map debug info"
                    accessibilityHint="Copies internal diagnostics to clipboard"
                  >
                    <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center', maxWidth: 320, marginTop: theme.spacing(1) }}>
                      Tap to copy debug info
                    </Caption>
                  </Pressable>
                )}
              </View>
            ) : (
              <>
                <View
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 40,
                    backgroundColor: theme.colors.primary + '10',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: theme.spacing(1),
                  }}
                >
                  <Ionicons name="map-outline" size={40} color={theme.colors.primary} />
                </View>
                <ThemedText variant="bodyStrong" style={{ fontSize: 20, textAlign: 'center', fontWeight: '700' }}>
                  No trips yet
                </ThemedText>
                <Body
                  color={theme.colors.textSecondary}
                  style={{ textAlign: 'center', fontSize: 15, lineHeight: 22, maxWidth: 280 }}
                >
                  Create your first trip to view map.
                </Body>
                {showStagingDebug && (
                  <Pressable
                    onPress={handleCopyMapDebug}
                    style={({ pressed }) => [{ opacity: pressed ? 0.7 : 1 }]}
                    accessibilityLabel="Copy map debug info"
                    accessibilityHint="Copies internal diagnostics to clipboard"
                  >
                    <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center', maxWidth: 320 }}>
                      Tap to copy debug info
                    </Caption>
                  </Pressable>
                )}
                <Button
                  label="Go to Trips"
                  variant="primary"
                  onPress={() => router.push('/(tabs)')}
                  icon={<Ionicons name="arrow-forward" size={18} color={theme.colorScheme === 'dark' ? '#000000' : '#FFFFFF'} />}
                  style={{ marginTop: theme.spacing(2), minWidth: 200 }}
                  accessibilityLabel="Go to trips tab"
                  accessibilityHint="Navigate to the trips tab to create your first trip"
                />
              </>
            )}
          </View>
        </Card>
      </Screen>
    );
  }

  const showPinsOrLocationsStatusOverlay = Boolean(
    selectedTripId &&
      selectedTrip &&
      (pinsLoading ||
        itineraryItemsLoading ||
        isGeocoding ||
        landmarksRefreshing ||
        geocodeRateLimited ||
        (selectedDay ? selectedDayMissingLocations > 0 : missingLocationItems.length > 0)),
  );

  const promptKey = navigationPrompt
    ? `${navigationPrompt.nextActivity?.title ?? ''}-${navigationPrompt.message ?? ''}`
    : null;

  return (
    <Screen
      title={hideHeader ? undefined : 'Maps'}
      subtitle={hideHeader ? undefined : 'View your itinerary and discover places'}
      scrollable={false}
      headerAction={hideHeader ? undefined : notificationButton}
      showOfflineIndicator={!hideHeader}
      disableTopInset={hideHeader}
    >
      <View style={styles.container}>
        {/* Trip Selector - place at top to avoid overlap */}
        {trips.length > 0 && (
          <View style={[styles.selectorContainer, hideHeader && styles.selectorContainerCompactTop]}>
            <SelectField
              // Save vertical space on mobile (we already show the selected value).
              label={Platform.OS === 'web' ? 'Select trip' : undefined}
              placeholder="Choose a trip"
              value={selectedTripId || undefined}
              options={tripOptions}
              onValueChange={(value) => setSelectedTripId(value)}
            />
          </View>
        )}
        {cachedMapsDescriptor ? (
          <View style={styles.offlineRow}>
            <View
              style={[
                styles.offlineChip,
                {
                  borderColor: theme.colors.borderMuted,
                  backgroundColor: theme.colors.backgroundSecondary,
                },
              ]}
            >
              <Ionicons name="cloud-offline-outline" size={14} color={theme.colors.textSecondary} />
              <Caption color={theme.colors.textSecondary}>{cachedMapsDescriptor}</Caption>
            </View>
          </View>
        ) : null}

        {!mapboxReady && (
          <Card variant="outlined" style={{ marginBottom: theme.spacing(2), backgroundColor: theme.colors.surface }}>
            <View style={{ gap: theme.spacing(1) }}>
              <ThemedText variant="bodyStrong">Maps unavailable</ThemedText>
              <Body color={theme.colors.textSecondary}>
                We can’t load the live map yet. You can still manage your itinerary and saved places.
              </Body>
              <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
                <Button
                  label="Setup"
                  variant="outlined"
                  onPress={() => setShowSetupModal(true)}
                  style={{ flex: 1 }}
                  size="small"
                />
                <Button
                  label="Retry"
                  onPress={handleRetryMapboxCheck}
                  style={{ flex: 1 }}
                  size="small"
                />
              </View>
            </View>
          </Card>
        )}

        {/* Mapbox diagnostics banner removed (it shrinks the map). */}

        {/* City Selector for Multi-City Trips (hidden when badges are shown on day pills) */}
        {showCitySelectorRow && (
          <View style={styles.citySelectorContainer}>
            <CitySelector
              destinations={tripDestinations}
              selectedCityIndex={selectedCityIndex}
              onSelectCity={setSelectedCityIndex}
              showDates={true}
              variant="pills"
            />
          </View>
        )}

        {/* Day Selector - show when trip has multiple days */}
        {tripDaysSafe.length > 1 && (
          <View style={[styles.daySelectorContainer, hideHeader && styles.daySelectorContainerCompactTop]}>
            <DaySelector
              tripDays={tripDaysSafe}
              selectedDayIndex={selectedDayIndex}
              showCityBadge={showCityBadges}
              onSelectDay={(index) => {
                if (index >= 0) {
                  const day = tripDaysSafe[index];
                  if (day?.cityIndex !== undefined && day.cityIndex !== null && day.cityIndex !== selectedCityIndex) {
                    setSelectedCityIndex(day.cityIndex);
                  }
                }
                setSelectedDayIndex(index);
                setUserHasInteractedWithMap(false); // Allow auto-fit for new day
                setCalloutPinId(null); // Reset inline callout when day changes
                setRecenterNonce((n) => n + 1);
                refreshPins(); // Re-geocode with the day's city context
              }}
              onSwipeDay={(direction) => {
                if (direction === 'prev') {
                  handleSwipeDayPrev();
                } else {
                  handleSwipeDayNext();
                }
                setUserHasInteractedWithMap(false);
                setCalloutPinId(null);
                setRecenterNonce((n) => n + 1);
              }}
              showAllDaysOption={true}
              variant="pills"
              compact={isMultiCityTrip}
            />
          </View>
        )}

        {/* Subtle helper text: addresses/locations are required for accurate pins */}
        {selectedDayIndex >= 0 && Platform.OS !== 'web' ? (
          <View style={styles.mapHintRow} pointerEvents="none">
            <Ionicons name="information-circle-outline" size={14} color={theme.colors.textSecondary} />
            <Caption color={theme.colors.textSecondary} numberOfLines={2}>
              Tip: add a precise address or place name to each activity for accurate pins.
            </Caption>
          </View>
        ) : null}

        {navigationPrompt && promptKey !== dismissedPromptKey && !showControls ? (
          <MapNextUpBar
            prompt={navigationPrompt}
            onGetDirections={handleGetDirections}
            onFocusActivity={() => {
              const pin = nextActivityPin;
              if (pin) handlePinPress(pin);
            }}
            onDismiss={() => {
              if (promptKey) setDismissedPromptKey(promptKey);
            }}
          />
        ) : null}

        <View style={styles.mapContainer}>
          {/* Web: tools top-right. Native: tools + pinning status on one row (avoids covering Tools). */}
          {Platform.OS === 'web' ? (
            <View style={[styles.topRightOverlayStack, { top: summaryOverlayTop }]} pointerEvents="box-none">
              <Pressable
                onPress={() => setShowControls(true)}
                style={({ pressed }) => [
                  styles.topRightOverlayChip,
                  useCompactWebToolsChip && styles.topRightOverlayChipCompact,
                  {
                    borderColor: theme.colors.borderMuted,
                    backgroundColor: theme.colors.surface,
                    opacity: pressed ? 0.8 : 1,
                  },
                ]}
                pointerEvents="auto"
                accessibilityLabel="Map tools"
                accessibilityHint="Opens map tools, including offline downloads"
              >
                <Ionicons name="options-outline" size={18} color={theme.colors.textSecondary} />
                {!useCompactWebToolsChip ? (
                  <Caption color={theme.colors.text} style={{ fontWeight: '700' }}>
                    Tools
                  </Caption>
                ) : null}
              </Pressable>
            </View>
          ) : (
            <View style={[styles.mobileMapTopChrome, { top: summaryOverlayTop }]} pointerEvents="box-none">
              <Pressable
                onPress={() => setShowControls(true)}
                style={({ pressed }) => [
                  styles.topRightOverlayChip,
                  {
                    flexShrink: 0,
                    borderColor: theme.colors.borderMuted,
                    backgroundColor: theme.colors.surface,
                    opacity: pressed ? 0.8 : 1,
                  },
                ]}
                pointerEvents="auto"
                accessibilityLabel="Map tools"
                accessibilityHint="Opens map tools, including offline downloads"
              >
                <Ionicons name="options-outline" size={18} color={theme.colors.textSecondary} />
                <Caption color={theme.colors.text} style={{ fontWeight: '700' }}>
                  Tools
                </Caption>
              </Pressable>
              {showPinsOrLocationsStatusOverlay ? (
                <View style={styles.mobilePinningStatusOuter} pointerEvents="box-none">
                  <View
                    style={[
                      styles.mobilePinningStatusPill,
                      {
                        backgroundColor: theme.colors.surface,
                        borderColor: theme.colors.borderMuted,
                      },
                    ]}
                    pointerEvents="auto"
                  >
                    {pinsLoading || itineraryItemsLoading || isGeocoding || landmarksRefreshing ? (
                      <ActivityIndicator size="small" color={theme.colors.primary} />
                    ) : (
                      <Ionicons name="information-circle-outline" size={14} color={theme.colors.textSecondary} />
                    )}
                    <Caption
                      color={theme.colors.textSecondary}
                      numberOfLines={1}
                      style={styles.mobilePinningStatusCaption}
                    >
                      {itineraryItemsLoading && !(pinsProgress?.total && isGeocoding)
                        ? 'Loading itinerary…'
                        : landmarksRefreshing
                          ? 'Refreshing landmarks…'
                        : isGeocodingPaused
                          ? 'Pinning paused'
                          : pinsProgress?.total && (pinsLoading || isGeocoding)
                            ? `Pinning… ${Math.min(pinsProgress.processed, pinsProgress.total)}/${pinsProgress.total}`
                            : selectedDay
                              ? `Pins ${selectedDayPinsOnMap}/${selectedDayActivityCount}`
                              : itineraryItemsWithLocationsCount > 0
                                ? `Pinned ${itineraryPinsPinnedCount}/${itineraryItemsWithLocationsCount}`
                                : 'Add locations for pins'}
                    </Caption>
                  </View>
                </View>
              ) : null}
            </View>
          )}

          {selectedTripId && showStagingDebug ? (
            <View
              style={[styles.landmarkDebugOverlay, { top: summaryOverlayTop + (Platform.OS === 'web' ? 46 : 56) }]}
              pointerEvents="none"
            >
              <View
                style={[
                  styles.landmarkDebugPill,
                  {
                    backgroundColor: theme.colors.surface,
                    borderColor: theme.colors.borderMuted,
                  },
                ]}
              >
                <Caption color={theme.colors.textSecondary} numberOfLines={1} style={styles.landmarkDebugText}>
                  {landmarkDebugText}
                </Caption>
                <Caption color={theme.colors.textSecondary} numberOfLines={1} style={styles.landmarkDebugQueryText}>
                  {landmarkDebugQueryText}
                </Caption>
              </View>
            </View>
          ) : null}

          {Platform.OS !== 'web' ? (
            <View
              style={[
                styles.mobileSummaryOverlay,
                {
                  top: undefined,
                  bottom: 12,
                  left: 12,
                  right: 12,
                  maxWidth: Math.max(0, screenWidth - 24),
                },
              ]}
              pointerEvents="box-none"
            >
              {selectedDay ? (
                <View style={styles.mobileSummaryRow}>
                  <View style={[styles.overlayCardWrapper, { width: miniSummaryWidth }]} pointerEvents="auto">
                    <DaySummaryCard
                      day={selectedDay}
                      weather={selectedDay ? mapDailyWeather[toDateKey(selectedDay.date) || ''] : null}
                      weatherStatus={weatherStatus}
                      weatherError={weatherError}
                      onRetryWeather={handleRetryWeather}
                      activities={selectedDayActivities}
                      pins={dayPinsForSummary}
                      totalDays={tripDaysSafe.length}
                      preferredTravelMode={preferredTravelMode}
                      onTapNextActivity={(activity) => {
                        const pin = pinsWithDistances.find(
                          (p) => p.type === 'itinerary' && (p.data as ItineraryItem)?.id === activity.id
                        );
                        if (pin) {
                          handlePinPress(pin);
                        }
                      }}
                      onTapAddActivity={() => {
                        if (selectedTripId) {
                          router.push(`/(tabs)/plan?tripId=${selectedTripId}&from=maps&focus=locations`);
                        }
                      }}
                      compact={true}
                      enableMapCollapse
                    />
                  </View>
                </View>
              ) : tripDaysSafe.length > 0 ? (
                <View style={styles.mobileSummaryRow}>
                  <View style={[styles.overlayCardWrapper, styles.mapSummaryCardStretch]} pointerEvents="auto">
                    {selectedDayIndex < 0 ? (
                      <DaySummaryCard
                        day={allDaysTripDay}
                        activities={itineraryItems || []}
                        pins={pinsWithDistances}
                        totalDays={tripDaysSafe.length}
                        compact={false}
                        preferredTravelMode={preferredTravelMode}
                        weatherByDateKey={mapDailyWeather}
                        weatherStatus={weatherStatus}
                        weatherError={weatherError}
                        onRetryWeather={handleRetryWeather}
                        onTapNextActivity={(activity) => {
                          const pin = pinsWithDistances.find(
                            (p) => p.type === 'itinerary' && (p.data as ItineraryItem)?.id === activity.id
                          );
                          if (pin) handlePinPress(pin);
                        }}
                        enableMapCollapse
                      />
                    ) : (
                      <DailyDistanceSummary
                        itineraryItems={itineraryItems || []}
                        pins={pinsWithDistances}
                        referencePointName={primaryReferencePoint?.title}
                        compact={false}
                      />
                    )}
                  </View>
                </View>
              ) : null}
            </View>
          ) : null}

          {/* Day Summary Card (web): overlay is fine on wide screens */}
          {Platform.OS === 'web' && selectedDay ? (
            <View style={[styles.dailySummaryWebContainer, { top: summaryOverlayTop }]} pointerEvents="box-none">
              <View style={styles.overlayCardWrapper}>
                <DaySummaryCard
                  day={selectedDay}
                  weather={selectedDay ? mapDailyWeather[toDateKey(selectedDay.date) || ''] : null}
                  weatherStatus={weatherStatus}
                  weatherError={weatherError}
                  onRetryWeather={handleRetryWeather}
                  activities={selectedDayActivities}
                  pins={dayPinsForSummary}
                  totalDays={tripDaysSafe.length}
                  preferredTravelMode={preferredTravelMode}
                  onTapNextActivity={(activity) => {
                    const pin = pinsWithDistances.find(
                      (p) => p.type === 'itinerary' && (p.data as ItineraryItem)?.id === activity.id
                    );
                    if (pin) {
                      handlePinPress(pin);
                    }
                  }}
                  onTapAddActivity={() => {
                    if (selectedTripId) {
                      router.push(`/(tabs)/plan?tripId=${selectedTripId}&from=maps&focus=locations`);
                    }
                  }}
                  compact={true}
                  footer={renderMissingPinsNotice(true)}
                />
              </View>
            </View>
          ) : Platform.OS === 'web' ? (
            <View style={[styles.dailySummaryWebContainer, { top: summaryOverlayTop }]} pointerEvents="box-none">
              <View style={styles.overlayCardWrapper}>
                {selectedDayIndex < 0 ? (
                  <DaySummaryCard
                    day={allDaysTripDay}
                    activities={itineraryItems || []}
                    pins={pinsWithDistances}
                    totalDays={tripDaysSafe.length}
                    compact={false}
                    preferredTravelMode={preferredTravelMode}
                    weatherByDateKey={mapDailyWeather}
                    weatherStatus={weatherStatus}
                    weatherError={weatherError}
                    onRetryWeather={handleRetryWeather}
                    onTapNextActivity={(activity) => {
                      const pin = pinsWithDistances.find(
                        (p) => p.type === 'itinerary' && (p.data as ItineraryItem)?.id === activity.id
                      );
                      if (pin) handlePinPress(pin);
                    }}
                  />
                ) : (
                  <DailyDistanceSummary
                    itineraryItems={itineraryItems || []}
                    pins={pinsWithDistances}
                    referencePointName={primaryReferencePoint?.title}
                  />
                )}
              </View>
            </View>
          ) : null}
          {/* Always render map - show empty state as overlay if needed */}
          {mapboxReady && (
            <MapView
              pins={pinsWithDayIndexes}
              routeArrows={[]}
              minZoomLevel={4}
              boundsPadding={mapBoundsPadding}
              boundsAnimationDurationMs={userHasInteractedWithMap ? 700 : 0}
              showIndexChips={selectedDayIndex >= 0}
              center={
                // Avoid any auto-centering while a callout is open to prevent panning away from the pin.
                calloutPinId || userHasInteractedWithMap
                  ? undefined
                  : (() => {
                    // Default behavior:
                    // - If we can fit bounds (first open), do that and don't force a center.
                    // - If we can't, center on next activity (if available), else trip center.
                    if (!userHasInteractedWithMap && !showFullTripView && !initialMapBounds && nextActivityPinForCentering) {
                      return {
                        latitude: nextActivityPinForCentering.coordinates.latitude + recenterNonce * 1e-9,
                        longitude: nextActivityPinForCentering.coordinates.longitude + recenterNonce * 1e-9,
                      };
                    }
                    return mapCenter
                      ? {
                        latitude: mapCenter.latitude + recenterNonce * 1e-9,
                        longitude: mapCenter.longitude + recenterNonce * 1e-9,
                      }
                      : mapCenter;
                  })()
              }
              // Default: fit to city-level pins. If the bounds are too large, we fall back to `center` + `zoomLevel`.
              // If “See whole trip” is enabled, prefer full-trip bounds.
              bounds={mapBounds}
              zoomLevel={mapZoomLevel}
              showUserLocation={pinFilters.user}
              onPinPress={(pin) => {
                setUserHasInteractedWithMap(true);
                handlePinPress(pin);
              }}
              onMapPress={() => {
                // Dismiss any open UI when user taps the map.
                Keyboard.dismiss();
                setCalloutPinId(null);
                setSelectedPin(null);
                setShowPinPreview(false);
                setShowPinModal(false);
                setUserHasInteractedWithMap(true);
              }}
              onZoomChange={(z) => {
                const clamped = Math.max(z, 4);
                // Don't treat programmatic camera updates during initial auto-fit (e.g. after switching trips)
                // as user interaction, otherwise auto-fit gets disabled before bounds are available.
                if (!pendingAutoFitRef.current && Date.now() > ignoreZoomEventsUntilRef.current) {
                  setUserHasInteractedWithMap(true);
                }
                // Skip setState during programmatic fitBounds/flyTo to avoid infinite update loops.
                if (Date.now() > ignoreZoomEventsUntilRef.current) {
                  setMapZoomLevel(clamped);
                }
              }}
              mapStyle={getMapboxStyleURL(mapStyle)}
              routes={[
                ...(showRoutes && itineraryRoutes.length > 0 ? itineraryRoutes : []),
                ...(currentRoute ? [currentRoute] : []),
              ]}
              calloutPinId={calloutPinId}
              renderCallout={renderPinCallout}
              onCloseCallout={() => setCalloutPinId(null)}
              primaryReferencePoint={primaryReferencePoint}
              walkingRadiusConfig={selectedTrip?.walkingRadiusConfig}
              clusters={clusters}
              zoomAdjustment={zoomAdjustment}
            />
          )}
          {!mapboxReady && (
            <View style={styles.mapFallback}>
              <MapEmptyState
                type="mapbox-missing"
                onAction={() => setShowSetupModal(true)}
                actionLabel="Setup Map"
                onSecondaryAction={handleRetryMapboxCheck}
              />
            </View>
          )}

          {/* Pins/loading overlay (non-blocking): show progress while pins are being derived/geocoded */}
          {Platform.OS === 'web' && showPinsOrLocationsStatusOverlay && (
            <View style={[styles.geocodingIndicator, { top: summaryOverlayTop }]} pointerEvents="box-none">
              <View
                style={[
                  styles.loadingCard,
                  {
                    backgroundColor: theme.colors.surface,
                    borderColor: theme.colors.borderMuted,
                  },
                ]}
              >
                {(pinsLoading || itineraryItemsLoading || isGeocoding || landmarksRefreshing) ? (
                  <ActivityIndicator size="small" color={theme.colors.primary} />
                ) : (
                  <Ionicons name="information-circle-outline" size={16} color={theme.colors.textSecondary} />
                )}
                <Caption color={theme.colors.textSecondary} style={styles.loadingText} numberOfLines={1}>
                  {itineraryItemsLoading && !(pinsProgress?.total && isGeocoding)
                    ? 'Loading itinerary…'
                    : landmarksRefreshing
                      ? 'Refreshing landmarks…'
                    : isGeocodingPaused
                      ? 'Pinning locations paused'
                      : pinsProgress?.total && (pinsLoading || isGeocoding)
                        ? `Pinning… ${Math.min(pinsProgress.processed, pinsProgress.total)}/${pinsProgress.total}`
                        : selectedDay
                          ? `Pins on map: ${selectedDayPinsOnMap}/${selectedDayActivityCount}`
                          : itineraryItemsWithLocationsCount > 0
                            ? `Locations pinned: ${itineraryPinsPinnedCount}/${itineraryItemsWithLocationsCount}`
                            : 'Add locations to see pins on the map'}
                </Caption>
                {(selectedDay ? selectedDayMissingLocations > 0 : missingLocationItems.length > 0) ? (
                  <Pressable
                    onPress={() => {
                      if (selectedTripId) {
                        router.push(`/(tabs)/plan?tripId=${selectedTripId}&from=maps&focus=locations`);
                      } else {
                        router.push('/(tabs)/plan?from=maps&focus=locations');
                      }
                    }}
                    style={({ pressed }) => [
                      styles.loadingActionChip,
                      {
                        backgroundColor: theme.colors.background,
                        borderColor: theme.colors.borderMuted,
                        opacity: pressed ? 0.8 : 1,
                      },
                    ]}
                  >
                    <Caption color={theme.colors.text} style={{ fontWeight: '700' }}>
                      Add locations
                    </Caption>
                  </Pressable>
                ) : null}
              </View>
            </View>
          )}

          {/* Empty State Overlay - only show when truly empty and not geocoding */}
          {emptyStateType && !isGeocoding && !pinsLoading && !emptyStateDismissed && (
            <View style={styles.emptyStateOverlay} pointerEvents="box-none">
              <Card
                variant="elevated"
                style={[
                  styles.emptyStateCard,
                  {
                    backgroundColor: theme.colors.surface + 'F2',
                    borderColor: theme.colors.borderMuted,
                  },
                ]}
              >
                <MapEmptyState
                  type={emptyStateType}
                  variant="card"
                  onAction={
                    emptyStateType === 'no-itinerary'
                      ? () => {
                        // Navigate to plan tab for the selected trip
                        if (selectedTripId) {
                          router.push(`/(tabs)/plan?tripId=${selectedTripId}&from=maps&focus=locations`);
                        } else {
                          router.push('/(tabs)/plan?from=maps&focus=locations');
                        }
                      }
                      : emptyStateType === 'no-pins'
                        ? () => {
                          if (selectedTripId) {
                            router.push(`/(tabs)/plan?tripId=${selectedTripId}&from=maps&focus=locations`);
                          } else {
                            router.push('/(tabs)/plan?from=maps&focus=locations');
                          }
                        }
                        : undefined
                  }
                  onSecondaryAction={
                    emptyStateType === 'no-itinerary'
                      ? () => {
                        setPinFilters((prev) => ({ ...prev, landmark: true }));
                        setEmptyStateDismissed(true);
                      }
                      : emptyStateType === 'no-pins'
                        ? () => {
                          setPinFilters((prev) => ({ ...prev, landmark: true }));
                          setEmptyStateDismissed(true);
                        }
                        : undefined
                  }
                />
              </Card>
            </View>
          )}

          {/* Geocoding loading indicator handled by unified pins/loading overlay above */}

          {/* Map Click Hint - First time user */}
          {!isMobile && <MapClickHint />}

          {mapboxReady && Platform.OS !== 'web' && !showControls && (
            <View style={[styles.mobileRightControlsColumn, { bottom: controlsBottomOffset }]} pointerEvents="box-none">
              <View style={styles.mobileQuickActionsStack}>
                <Pressable
                  onPress={() => setShowMobileQuickActions(!showMobileQuickActions)}
                  style={({ pressed }) => [
                    styles.mobileQuickActionButton,
                    styles.mobileQuickActionPrimary,
                    {
                      backgroundColor: theme.colors.surface,
                      borderColor: theme.colors.borderMuted,
                      opacity: pressed ? 0.8 : 1,
                    },
                  ]}
                  accessibilityLabel={showMobileQuickActions ? 'Collapse quick map actions' : 'Expand quick map actions'}
                >
                  <Ionicons
                    name={showMobileQuickActions ? 'chevron-down' : 'ellipsis-horizontal'}
                    size={18}
                    color={theme.colors.text}
                  />
                </Pressable>

                {showMobileQuickActions ? (
                  <>
                    <Pressable
                      onPress={handleZoomIn}
                      style={({ pressed }) => [
                        styles.mobileQuickActionButton,
                        styles.mobileQuickActionPrimary,
                        {
                          backgroundColor: theme.colors.surface,
                          borderColor: theme.colors.borderMuted,
                          opacity: pressed ? 0.8 : 1,
                        },
                      ]}
                      accessibilityLabel="Zoom in"
                    >
                      <Ionicons name="add" size={18} color={theme.colors.text} />
                    </Pressable>
                    <Pressable
                      onPress={handleZoomOut}
                      style={({ pressed }) => [
                        styles.mobileQuickActionButton,
                        styles.mobileQuickActionPrimary,
                        {
                          backgroundColor: theme.colors.surface,
                          borderColor: theme.colors.borderMuted,
                          opacity: pressed ? 0.8 : 1,
                        },
                      ]}
                      accessibilityLabel="Zoom out"
                    >
                      <Ionicons name="remove" size={18} color={theme.colors.text} />
                    </Pressable>
                    <Pressable
                      onPress={() => setShowMapStyleSelector(true)}
                      style={({ pressed }) => [
                        styles.mobileQuickActionButton,
                        styles.mobileQuickActionPrimary,
                        {
                          backgroundColor: theme.colors.surface,
                          borderColor: theme.colors.borderMuted,
                          opacity: pressed ? 0.8 : 1,
                        },
                      ]}
                      accessibilityLabel="Map style"
                    >
                      <Ionicons name="layers-outline" size={18} color={theme.colors.text} />
                    </Pressable>
                    <Pressable
                      onPress={handleOptimizeItinerary}
                      style={({ pressed }) => [
                        styles.mobileQuickActionButton,
                        styles.mobileQuickActionPrimary,
                        {
                          backgroundColor: theme.colors.surface,
                          borderColor: theme.colors.borderMuted,
                          opacity: pressed ? 0.8 : 1,
                        },
                      ]}
                      accessibilityLabel="Optimize route"
                    >
                      <Ionicons name="swap-horizontal" size={18} color={theme.colors.text} />
                    </Pressable>
                  </>
                ) : null}
              </View>

              <Pressable
                onPress={handleFitDay}
                style={({ pressed }) => [
                  styles.mobileQuickActionButton,
                  styles.mobileQuickActionSecondary,
                  {
                    backgroundColor: theme.colors.surface,
                    borderColor: theme.colors.borderMuted,
                    opacity: pressed ? 0.8 : 1,
                  },
                ]}
                accessibilityLabel="Fit map to day"
              >
                <Ionicons name="scan-outline" size={18} color={theme.colors.text} />
              </Pressable>
            </View>
          )}

          {mapboxReady && Platform.OS === 'web' && (
            <>
              <View style={[styles.rightControlsStack, { bottom: controlsBottomOffset }]} pointerEvents="box-none">
                <Pressable
                  onPress={handleFitDay}
                  style={({ pressed }) => [
                    styles.rightControlButton,
                    {
                      backgroundColor: theme.colors.primary,
                      borderColor: 'transparent',
                      opacity: pressed ? 0.82 : 1,
                    },
                  ]}
                  accessibilityLabel="Fit map to day"
                >
                  <Ionicons name="scan-outline" size={18} color={theme.colors.surface} />
                </Pressable>
                <Pressable
                  onPress={handleZoomIn}
                  style={({ pressed }) => [
                    styles.rightControlButton,
                    {
                      backgroundColor: theme.colors.surface,
                      borderColor: theme.colors.borderMuted,
                      opacity: pressed ? 0.8 : 1,
                    },
                  ]}
                  accessibilityLabel="Zoom in"
                >
                  <Ionicons name="add" size={18} color={theme.colors.text} />
                </Pressable>
                <Pressable
                  onPress={handleZoomOut}
                  style={({ pressed }) => [
                    styles.rightControlButton,
                    {
                      backgroundColor: theme.colors.surface,
                      borderColor: theme.colors.borderMuted,
                      opacity: pressed ? 0.8 : 1,
                    },
                  ]}
                  accessibilityLabel="Zoom out"
                >
                  <Ionicons name="remove" size={18} color={theme.colors.text} />
                </Pressable>
              </View>

              {/* Removed the green recenter FAB; Fit day now performs the same action */}
            </>
          )}

          {/* Removed bottom route action bar to free space; controls moved to right stack */}

          {/* Removed bottom navigation prompt card — use MapNextUpBar above the map instead */}
        </View>

        {shouldUseSheet && hasSheetContent && (
          <View style={styles.sheetContainer}>
            {renderNativeSheet && BottomSheetViewComponent ? (
              <BottomSheetComponent
                ref={bottomSheetRef}
                snapPoints={bottomSheetSnapPoints}
                index={initialSheetIndex}
                enablePanDownToClose
                onClose={() => {
                  setShowPinModal(false);
                  setShowPinPreview(false);
                  setSelectedPin(null);
                  setDirectionsDestination(null);
                  // If the sheet was showing the navigation prompt, allow the user to fully dismiss it.
                  setShowNavigationPrompt(false);
                }}
                backgroundStyle={{ backgroundColor: theme.colors.surface }}
                handleStyle={{ backgroundColor: theme.colors.surface }}
              >
                <BottomSheetViewComponent accessibilityViewIsModal>
                  {renderSheetContent()}
                </BottomSheetViewComponent>
              </BottomSheetComponent>
            ) : (
              <Animated.View
                style={[
                  styles.sheetFallback,
                  {
                    paddingBottom: insets.bottom + theme.spacing(2),
                    backgroundColor: theme.colors.surface,
                    borderColor: theme.colors.borderMuted,
                    height: fallbackHeights[fallbackSheetState],
                    transform: [{ translateY: fallbackPan }],
                  },
                ]}
                {...fallbackPanResponder.panHandlers}
              >
                <Pressable
                  onPress={() => {
                    const order: Array<'peek' | 'half' | 'full'> = ['peek', 'half', 'full'];
                    const currentIndex = order.indexOf(fallbackSheetState);
                    const next = order[(currentIndex + 1) % order.length];
                    setFallbackSheetState(next);
                  }}
                  style={{ alignItems: 'center', paddingVertical: 4 }}
                  accessibilityLabel="Expand or collapse sheet"
                >
                  <View style={styles.sheetHandle} />
                </Pressable>
                {fallbackSheetState === 'peek' ? (
                  <View style={{ paddingHorizontal: 12, paddingBottom: 8 }}>
                    <Caption color={theme.colors.textSecondary}>Pull up for details</Caption>
                  </View>
                ) : (
                  <ScrollView
                    style={{ flex: 1 }}
                    contentContainerStyle={{ paddingHorizontal: 8, paddingBottom: 12 }}
                    showsVerticalScrollIndicator={false}
                  >
                    {renderSheetContent()}
                  </ScrollView>
                )}
              </Animated.View>
            )}
          </View>
        )}

        {/* Pin Quick Preview - Mobile only */}
        {!shouldUseSheet && selectedPin && Platform.OS !== 'web' && (
          <PinQuickPreview
            pin={selectedPin}
            visible={showPinPreview}
            onClose={() => {
              setShowPinPreview(false);
              setSelectedPin(null);
            }}
            onViewDetails={() => {
              // “View details” for itinerary pins should take users to the itinerary item (Plan tab),
              // not open another map/directions modal.
              if (selectedPin.type === 'itinerary') {
                const itemId =
                  (selectedPin.data as ItineraryItem | undefined)?.id ??
                  (selectedPin.id.startsWith('itinerary-') ? selectedPin.id.replace(/^itinerary-/, '') : undefined);

                if (selectedTripId && itemId) {
                  setShowPinPreview(false);
                  setSelectedPin(null);
                  setCalloutPinId(null);
                  router.push(`/(tabs)/plan?tripId=${selectedTripId}&from=maps&itemId=${itemId}`);
                  return;
                }
              }

              // Non-itinerary pins still use the in-app detail modal.
              setShowPinPreview(false);
              setShowPinModal(true);
            }}
            onGetDirections={() => handleGetDirectionsFromPin(selectedPin)}
            onAddToItinerary={(selectedPin.type === 'landmark' || selectedPin.type === 'search') ? () => handleAddToItinerary(selectedPin) : undefined}
            primaryReferencePoint={primaryReferencePoint}
            distance={selectedPin.distanceMeta?.distance}
          />
        )}

        {/* Pin Detail Modal */}
        {!shouldUseSheet && selectedPin && (
          <PinDetailModal
            visible={showPinModal}
            pin={selectedPin}
            onClose={() => {
              setShowPinModal(false);
              setSelectedPin(null);
            }}
            onGetDirections={handleGetDirectionsFromPin}
            onAddToItinerary={(selectedPin.type === 'landmark' || selectedPin.type === 'search') ? handleAddToItinerary : undefined}
            referencePoints={selectedTrip?.referencePoints}
            primaryReferencePoint={primaryReferencePoint}
            distanceDisplayMode={selectedTrip?.distanceDisplayMode}
            tripId={selectedTrip?.id}
          />
        )}

        {/* Directions Panel */}
        {showDirectionsPanel && directionsDestination && (
          <DirectionsPanel
            visible={showDirectionsPanel}
            start={currentLocation || mapCenter}
            destination={directionsDestination.coordinates}
            destinationAddressQuery={buildDirectionsDestinationQuery(directionsDestination)}
            destinationName={directionsDestination.title}
            onClose={handleCloseDirectionsPanel}
            onOpenExternalMaps={handleOpenExternalMaps}
            preCalculatedRoute={calculatedRoute}
          />
        )}

        {/* Itinerary Item Form for adding landmarks */}
        {selectedTrip && firebaseUser?.uid && landmarkToAdd && (
          <ItineraryItemForm
            key={landmarkToAdd.id} // Force re-render when landmark changes
            visible={showItineraryForm}
            onClose={() => {
              setShowItineraryForm(false);
              setLandmarkToAdd(null);
            }}
            onSubmit={handleItineraryFormSubmit}
            tripId={selectedTrip.id}
            userId={firebaseUser.uid}
            defaultDate={selectedTrip.startDate}
            tripStartDate={selectedTrip.startDate}
            tripEndDate={selectedTrip.endDate}
            tripDestinations={selectedTrip.destinations}
            initialItem={{
              title: landmarkToAdd.title,
              description: (landmarkToAdd.data as Place)?.description || undefined,
              category: 'other',
              startDate: selectedTrip.startDate,
              // Always set address if available from Place data, otherwise use pin title as location hint
              address: (landmarkToAdd.data as Place)?.address ||
                (landmarkToAdd.subtitle ? `${landmarkToAdd.title}, ${landmarkToAdd.subtitle}` : landmarkToAdd.title) ||
                undefined,
              // Always set location - prefer Place address, then pin title, then coordinates as fallback
              location: (landmarkToAdd.data as Place)?.address ||
                landmarkToAdd.title ||
                `${landmarkToAdd.coordinates.latitude}, ${landmarkToAdd.coordinates.longitude}`,
              source: 'manual',
              status: 'planned',
              createdBy: firebaseUser.uid,
              documents: [],
            } as any}
          />
        )}

        {/* Trip Picker Modal */}
        <Modal
          visible={showTripPicker}
          transparent
          animationType="fade"
          onRequestClose={() => setShowTripPicker(false)}
          accessibilityViewIsModal
        >
          <Pressable
            style={{ flex: 1 }}
            onPress={() => setShowTripPicker(false)}
            {...getDismissA11yProps('trip picker')}
          />
          <View style={styles.tripPickerSheet}>
            <Card variant="elevated" style={{ padding: theme.spacing(2), gap: theme.spacing(1.5) }}>
              <View ref={tripPickerHeadingRef} accessible accessibilityRole="header">
                <ThemedText variant="h3" style={{ fontSize: 16, fontWeight: '700' }}>
                  Select trip
                </ThemedText>
              </View>
              <SelectField
                label="Trip"
                placeholder="Choose a trip"
                value={selectedTripId || undefined}
                options={tripOptions}
                onValueChange={(value) => {
                  setSelectedTripId(value);
                  setShowTripPicker(false);
                }}
              />
              <Button label="Close" variant="secondary" onPress={() => setShowTripPicker(false)} />
            </Card>
          </View>
        </Modal>

        {/* Map Controls Panel */}
        <MapControlsPanel
          visible={showControls}
          onClose={() => setShowControls(false)}
          pinFilters={pinFilters}
          onPinFilterChange={setPinFilters}
          pinCounts={pinCounts}
          mustSeeOnly={mustSeeOnly}
          mustSeeCount={mustSeeCount}
          onToggleMustSeeOnly={setMustSeeOnly}
          alwaysShowMustSee={alwaysShowMustSee}
          onToggleAlwaysShowMustSee={setAlwaysShowMustSee}
          onRefreshPins={() =>
            refreshPins(selectedDayKey, { onlyPriorityDay: false, forceFreshGeocode: true })
          }
          repinItineraryInProgress={isGeocoding}
          onRefreshLandmarkSuggestions={refreshLandmarkSuggestions}
          landmarksRefreshing={landmarksRefreshing}
          showingCachedLandmarks={showingCachedLandmarks}
          showingBaselineLandmarks={showingBaselineLandmarks}
          onToggleOfflineDownload={() => {
            if (!isPremium && !isDownloaded) {
              openPremiumGate({
                feature: 'offline_maps',
                title: 'Offline maps',
                message: 'Offline maps are a Premium feature. Upgrade to download maps for offline use.',
                source: 'feature_gate',
              });
              return;
            }
            setShowOfflineDownload(!showOfflineDownload);
          }}
          showOfflineDownload={showOfflineDownload}
          isDownloaded={isDownloaded}
          offlineCity={selectedCityDestination?.city || selectedTrip?.destination}
          onOfflineDownload={async () => {
            setIsDownloaded(true);
            try {
              const { getDownloadedRegions, findDownloadedRegionForCity } = await import('@/services/offlineMaps');
              const regions = await getDownloadedRegions();
              const region = findDownloadedRegionForCity(
                regions,
                selectedCityDestination?.city || selectedTrip?.destination,
              );
              setIsDownloaded(!!region);
            } catch (error) {
              console.error('[TripMapExperience] Error refreshing download status:', error);
            }
          }}
          onOfflineDelete={async () => {
            setIsDownloaded(false);
            try {
              const { getDownloadedRegions, findDownloadedRegionForCity } = await import('@/services/offlineMaps');
              const regions = await getDownloadedRegions();
              const region = findDownloadedRegionForCity(
                regions,
                selectedCityDestination?.city || selectedTrip?.destination,
              );
              setIsDownloaded(!!region);
            } catch (error) {
              console.error('[TripMapExperience] Error refreshing download status:', error);
            }
          }}
          onOpenMapStyleSelector={() => setShowMapStyleSelector(true)}
          onOptimizeItinerary={handleOptimizeItinerary}
          itineraryItemsCount={itineraryItems?.length || 0}
          onToggleFullTripView={
            fullTripBounds
              ? () => setShowFullTripView((prev) => !prev)
              : undefined
          }
          isFullTripViewActive={showFullTripView}
          isMultiCityTrip={isMultiCityTrip}
          referencePoints={selectedTrip?.referencePoints}
          primaryReferencePointId={selectedTrip?.primaryReferencePointId}
          distanceDisplayMode={selectedTrip?.distanceDisplayMode}
          onSelectReferencePoint={handleSelectReferencePoint}
          onCreateReferencePoint={userId ? handleCreateReferencePoint : undefined}
          onDistanceDisplayModeChange={handleDistanceDisplayModeChange}
          onToggleRouteVisualization={() => setShowRoutes((prev) => !prev)}
          showRoutes={showRoutes}
          routeMode={routeMode}
          onRouteModeChange={setRouteMode}
        />

        {/* Map Style Selector */}
        <MapStyleSelector
          visible={showMapStyleSelector}
          currentStyle={mapStyle}
          onSelectStyle={setMapStyle}
          onClose={() => setShowMapStyleSelector(false)}
        />

        {/* Mapbox Setup Modal */}
        <MapboxSetupModal
          visible={showSetupModal}
          onDismiss={() => setShowSetupModal(false)}
          onDontShowAgain={() => setShowSetupModal(false)}
        />

        {/* Route Optimization Modal */}
        {itineraryItems && itineraryItems.length >= 3 && (
          <RouteOptimizationModal
            visible={showRouteOptimization}
            itineraryItems={itineraryItems}
            // Use a trip/city anchor for optimization (NOT the user's current GPS),
            // otherwise optimization can “teleport” the route when the user is not in-destination yet.
            startLocation={primaryReferencePoint?.coordinates || mapCenter || undefined}
            onClose={() => setShowRouteOptimization(false)}
            onApplyOptimization={handleApplyOptimization}
          />
        )}

      </View>
    </Screen>
  );
}

export function TripMapPreview({
  tripId,
  height = 220,
  onOpenFullMap,
  title = 'Trip map & key places',
  description = 'See your planned stops, trendy picks, and travel-time nudges.',
  variant = 'card',
}: TripMapPreviewProps) {
  const theme = useTheme();
  const chipBackground =
    theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)';
  const {
    selectedTrip,
    mapPins,
    mapCenter,
    navigationPrompt,
    landmarks,
    itineraryItems,
    handleGetDirections,
  } = useTripMapData({
    initialTripId: tripId ?? null,
    controlledTripId: tripId ?? null,
    mode: 'preview',
  });

  const promptMeta = useMemo(() => {
    if (!navigationPrompt) return null;
    return {
      travelMinutes:
        navigationPrompt.travelDurationMinutes ??
        Math.round(navigationPrompt.fastestRoute.duration / 60),
      leaveInMinutes:
        navigationPrompt.leaveInMinutes >= 0
          ? navigationPrompt.leaveInMinutes
          : Math.max(
            0,
            Math.round(
              (navigationPrompt.recommendedDepartureTime.getTime() - Date.now()) / 60000,
            ),
          ),
      activityTitle: navigationPrompt.nextActivity.title,
    };
  }, [navigationPrompt]);

  const emptyState = (
    <View
      style={[
        previewStyles.placeholder,
        {
          borderColor: theme.colors.borderMuted,
          height,
        },
      ]}
    >
      <Caption color={theme.colors.textSecondary}>
        Select a trip to preview its map and travel prompts.
      </Caption>
    </View>
  );

  if (!selectedTrip) {
    return variant === 'card' ? (
      <Card variant="outlined" style={{ borderRadius: theme.radii.lg, padding: theme.spacing(2) }}>
        {emptyState}
      </Card>
    ) : (
      emptyState
    );
  }

  const content = (
    <View
      style={{
        padding: variant === 'card' ? theme.spacing(2) : 0,
        gap: theme.spacing(1.5),
      }}
    >
      <View style={previewStyles.header}>
        <View style={{ flex: 1, minWidth: 0 }}>
          <ThemedText variant="h3" style={{ fontSize: 16, fontWeight: '700' }}>
            {title}
          </ThemedText>
          <Caption color={theme.colors.textSecondary}>{description}</Caption>
        </View>
        {onOpenFullMap && (
          <Button
            label="Open map"
            onPress={onOpenFullMap}
            size="small"
            style={{ alignSelf: 'flex-start', flexShrink: 0 }}
          />
        )}
      </View>

      <Pressable
        onPress={onOpenFullMap}
        disabled={!onOpenFullMap}
        style={({ pressed }) => [
          previewStyles.mapWrapper,
          {
            height,
            borderColor: theme.colors.borderMuted,
            backgroundColor: theme.colors.surface,
            opacity: onOpenFullMap && pressed ? 0.96 : 1,
          },
        ]}
      >
        <MapView
          pins={mapPins}
          center={mapCenter}
          zoomLevel={selectedTrip?.destination ? 11 : 2}
          // Preview always focuses the current trip, not whole-trip bounds
          showUserLocation
          style={{ flex: 1 }}
        />
        {promptMeta && (
          <Pressable
            onPress={handleGetDirections}
            style={({ pressed }) => [
              previewStyles.promptPill,
              {
                backgroundColor: theme.colors.surface,
                opacity: pressed ? 0.85 : 1,
              },
            ]}
          >
            <Ionicons name="time-outline" size={18} color={theme.colors.primary} />
            <View style={{ flex: 1 }}>
              <ThemedText variant="bodyStrong" style={{ fontSize: 13 }}>
                Leave in {promptMeta.leaveInMinutes} min
              </ThemedText>
              <Caption color={theme.colors.textSecondary}>
                {promptMeta.activityTitle} • {promptMeta.travelMinutes} min away
              </Caption>
            </View>
            <Ionicons name="navigate-outline" size={18} color={theme.colors.primary} />
          </Pressable>
        )}
      </Pressable>

      <View style={previewStyles.metaRow}>
        <View
          style={[
            previewStyles.metaChip,
            {
              backgroundColor: chipBackground,
            },
          ]}
        >
          <Ionicons name="flag-outline" size={16} color={theme.colors.primary} />
          <Caption color={theme.colors.textSecondary}>
            {itineraryItems.length} itinerary pins
          </Caption>
        </View>
        <View
          style={[
            previewStyles.metaChip,
            {
              backgroundColor: chipBackground,
            },
          ]}
        >
          <Ionicons name="star-outline" size={16} color={theme.colors.primary} />
          <Caption color={theme.colors.textSecondary}>
            {landmarks.length} trendy spots
          </Caption>
        </View>
      </View>
    </View>
  );

  if (variant === 'card') {
    return (
      <Card variant="outlined" style={{ borderRadius: theme.radii.lg }}>
        {content}
      </Card>
    );
  }

  return content;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  selectorContainer: {
    paddingHorizontal: 16,
    paddingTop: 0,
    paddingBottom: 0,
    // Keep below the Screen offline banner; negative margin pulled the trip picker over the red card.
    marginTop: 0,
    gap: 4,
    zIndex: 100,
  },
  selectorContainerCompactTop: {
    marginTop: 2,
  },
  offlineRow: {
    paddingHorizontal: 16,
    paddingTop: 4,
    paddingBottom: 4,
    alignItems: 'flex-start',
    zIndex: 101,
  },
  searchRow: {
    paddingHorizontal: 16,
    paddingTop: 4,
    paddingBottom: 4,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    zIndex: 101,
  },
  topRightOverlayStack: {
    position: 'absolute',
    right: 16,
    zIndex: 110,
    gap: 8,
    alignItems: 'flex-end',
  },
  mobileMapTopChrome: {
    position: 'absolute',
    left: 12,
    right: 12,
    zIndex: 110,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  mobilePinningStatusOuter: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  mobilePinningStatusPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    maxWidth: '100%',
    flexShrink: 1,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.08,
        shadowRadius: 2,
      },
      android: {
        elevation: 2,
      },
      default: {},
    }),
  },
  mobilePinningStatusCaption: {
    flexShrink: 1,
    minWidth: 0,
    fontSize: 12,
  },
  topRightOverlayChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 8,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.08,
        shadowRadius: 2,
      },
      android: {
        elevation: 2,
      },
      default: {},
    }),
  },
  topRightOverlayChipCompact: {
    paddingHorizontal: 10,
    gap: 0,
  },
  landmarkDebugOverlay: {
    position: 'absolute',
    right: 12,
    zIndex: 109,
    alignItems: 'flex-end',
  },
  landmarkDebugPill: {
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 5,
    maxWidth: 290,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.06,
        shadowRadius: 2,
      },
      android: {
        elevation: 1,
      },
      default: {},
    }),
  },
  landmarkDebugText: {
    fontSize: 11,
  },
  landmarkDebugQueryText: {
    fontSize: 10,
    opacity: 0.9,
  },
  searchRowSearch: {
    flex: 1,
    minWidth: 180,
    minHeight: 44,
    alignSelf: 'stretch',
    justifyContent: 'center',
  },
  offlineChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 12,
    borderWidth: 1,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.08,
        shadowRadius: 2,
      },
      android: {
        elevation: 2,
      },
      default: {},
    }),
  },
  citySelectorContainer: {
    backgroundColor: 'transparent',
    zIndex: 99,
  },
  daySelectorContainer: {
    backgroundColor: 'transparent',
    zIndex: 98,
    marginTop: 0,
  },
  daySelectorContainerCompactTop: {
    marginTop: -2,
  },
  mapHintRow: {
    paddingHorizontal: 16,
    paddingTop: 0,
    paddingBottom: 2,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    zIndex: 98,
  },
  mapContainer: {
    flex: 1,
    position: 'relative',
    ...(Platform.OS === 'web' ? {
      minHeight: 300,
      width: '100%',
      height: '100%',
    } : {}),
  },
  mapFallback: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  emptyStateOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    // Non-blocking overlay: keep map (and landmarks) visible and pinch-zoomable.
    // We only render a compact card users can dismiss.
    paddingHorizontal: 16,
    paddingTop: 12,
    backgroundColor: 'transparent',
    zIndex: 10,
  },
  emptyStateCard: {
    // Keep it out of the way: a small banner near the top.
    alignSelf: 'center',
    width: Platform.OS === 'web' ? 380 : '100%',
    maxWidth: 420,
    borderWidth: 1,
  },
  headerActions: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  headerButton: {
    padding: 8,
  },
  searchBarContainer: {
    position: 'absolute',
    top: Platform.OS === 'web' ? 6 : 6,
    left: 16,
    right: 16,
    width: Platform.OS === 'web' ? 340 : undefined,
    zIndex: 1000,
    // Layout helper for the top control row.
  },
  mapControlsButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    zIndex: 1001,
    flexShrink: 0,
  },
  rightControlsStack: {
    position: 'absolute',
    right: 16,
    bottom: 160,
    gap: 10,
    zIndex: 998,
    alignItems: 'flex-end',
  },
  rightControlButton: {
    width: 44,
    height: 44,
    borderRadius: 14,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.15,
        shadowRadius: 4,
      },
      android: {
        elevation: 4,
      },
      default: {},
    }),
  },
  geocodingIndicator: {
    position: 'absolute',
    top: 80,
    left: 16,
    right: 16,
    zIndex: 999,
    alignItems: 'center',
  },
  loadingCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 12,
    borderWidth: 1,
    maxWidth: '100%',
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3,
    }),
  },
  loadingText: {
    marginLeft: 2,
    flex: 1,
  },
  loadingActionChip: {
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 6,
    flexShrink: 0,
  },
  routeActionsContainer: {
    position: 'absolute',
    bottom: 80, // Align with FAB button area (FAB is at bottom: 16, height: 56, so buttons at 80)
    left: 16,
    right: 80, // Leave space for FAB on the right (FAB width: 56 + margin: 16 = 72, use 80 for safety)
    flexDirection: 'row',
    gap: 8,
    zIndex: 998, // Below FAB menu but above map
  },
  routeActionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 12,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.2,
        shadowRadius: 4,
      },
      android: {
        elevation: 4,
      },
      default: {},
    }),
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    } : {}),
  },
  routeActionButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  mobileRightControlsColumn: {
    position: 'absolute',
    right: 16,
    zIndex: 998,
    alignItems: 'flex-end',
    gap: 12,
  },
  mobileQuickActionsStack: {
    alignItems: 'flex-end',
    gap: 8,
  },
  mobileQuickActionButton: {
    width: 48,
    height: 48,
    borderRadius: 16,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.15,
        shadowRadius: 4,
      },
      android: {
        elevation: 4,
      },
      default: {},
    }),
  },
  mobileQuickActionPrimary: {
    backgroundColor: '#1F1F1F',
  },
  mobileQuickActionSecondary: {
    backgroundColor: '#FFFFFF',
  },
  navigationPromptContainer: {
    position: 'absolute',
    bottom: 16,
    left: 16,
    right: 16,
    zIndex: 998,
  },
  bottomRecenterButton: {
    position: 'absolute',
    bottom: Platform.OS === 'web' ? 24 : 28,
    right: 16,
    width: 50,
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#0B3F1A',
    borderWidth: 0,
    zIndex: 998,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 4px rgba(0, 0, 0, 0.25)' }
      : {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 4,
        elevation: 5,
      }),
  },
  tripPickerSheet: {
    position: 'absolute',
    bottom: Platform.OS === 'web' ? 120 : 80,
    left: 16,
    right: 16,
  },
  mobileSummaryOverlay: {
    position: 'absolute',
    top: 64,
    left: 12,
    right: 12,
    zIndex: 120,
    alignItems: 'flex-start',
  },
  mobileSummaryRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 10,
    justifyContent: 'flex-start',
  },
  mapSummaryCardStretch: {
    width: '100%',
    maxWidth: '100%',
  },
  missingPinsPill: {
    width: 64,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.08)',
    backgroundColor: 'rgba(255,255,255,0.92)',
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },

  dailySummaryWebContainer: {
    position: 'absolute',
    top: 64,
    left: 12,
    right: undefined,
    zIndex: 999,
    maxWidth: 400,
    ...(Platform.OS === 'web' ? {
      // Keep it pinned to the top-left; avoid reserving right-side space which can push it inward.
    } : {}),
  },
  overlayCardWrapper: {
    borderRadius: 16,
    // Keep wrapper purely for spacing; Card provides its own surface/shadow.
    backgroundColor: 'transparent',
    padding: 0,
  },
  dailySummaryInlineContainer: {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 8,
    marginTop: 8,
    zIndex: 95,
    gap: 6,
  },
  missingPinsNotice: {
    marginTop: 6,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.08)',
    backgroundColor: 'rgba(0,0,0,0.03)',
  },
  missingPinsNoticeEmbedded: {
    marginTop: 8,
  },
  missingPinsText: {
    flex: 1,
    gap: 2,
  },
  sheetContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2002,
  },
  sheetFallback: {
    marginHorizontal: 16,
    marginBottom: 12,
    borderRadius: 16,
    backgroundColor: '#FFFFFF',
    padding: 16,
    borderWidth: 1,
    overflow: 'hidden',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 10,
      },
      android: {
        elevation: 6,
      },
      default: {},
    }),
  },
  sheetContent: {
    gap: 12,
    paddingHorizontal: 16,
    paddingTop: 12,
  },
  sheetHandle: {
    alignSelf: 'center',
    width: 48,
    height: 4,
    borderRadius: 4,
    backgroundColor: '#C7C7CC',
    marginBottom: 8,
  },
  modeSegment: {
    flexDirection: 'row',
    gap: 8,
  },
  modePill: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const previewStyles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  mapWrapper: {
    borderWidth: 1,
    borderRadius: 16,
    overflow: 'hidden',
  },
  promptPill: {
    position: 'absolute',
    left: 12,
    right: 12,
    bottom: 12,
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  metaRow: {
    flexDirection: 'row',
    gap: 12,
  },
  metaChip: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 999,
  },
  placeholder: {
    borderWidth: 1,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
});

